// ============================================================================
// SizeCheckModal.jsx (MAINTAINED VERSION - LIVE CODE)
// 
// HOUSE CLEANING NOTES:
// - Reorganized into clear, labeled sections for maintainability
// - Added detailed section headers and boundaries
// - Preserved ALL existing functionality - NO LOGIC CHANGES
// - Grouped related constants, state, and effects together
// - Added visual separators for surgical editing
// ============================================================================
import { downloadProductOrderConfig } from "./DownloadText";
import React, { useEffect, useMemo, useState, Suspense } from "react";
import { Modal, Button, SelectInput, TextInput } from "./UIComponents";
import { PackingCalculator } from "./PackingCalculator";
// NEW: Import HDSD Data
import HDSD_DATA from "../pages/hdsd.json";

// ============================================================================
// LAZY LOADED COMPONENTS - START
// ============================================================================
const PriceModal = React.lazy(() => import("./RetailModal"));
const HeadlessFstModal = React.lazy(() => import("./FstPriceModal")); // Consolidated
const CartModal = React.lazy(() => import("./CartModal"));
// ============================================================================
// LAZY LOADED COMPONENTS - END
// ============================================================================


// ============================================================================
// UTILITY FUNCTIONS - START
// ============================================================================
const isTextInput = (s) =>
  String(s || "").trim().toLowerCase() === "text input var char";
const isDigitInput = (s) =>
  String(s || "").trim().toLowerCase() === "text input digit";
const isFormToken = (s) =>
  String(s || "").trim().toLowerCase().startsWith("form.");
const splitChoices = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

// Final validation for 2-decimal positive numbers (e.g., "123", "123.4", "123.45")
const isValid2dpNumber = (v) => /^\d+(\.\d{1,2})?$/.test(String(v).trim());

/** Coerce string (possibly with decimals) into integer millimeters safely */
const toIntMm = (v) => {
  const n = Number(String(v ?? "").trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
};

/* OP_EXW_MAP handled server-side in /api/data.js (action = retail_exw) */

/** Map form.* tokens to actual values (as strings)
 * NOTE: Only form.prod_label is supported now
 */
function valueFromFormToken(token, ctx) {
  const t = String(token || "").trim().toLowerCase();
  if (t === "form.prod_label") return String(ctx.prod_label ?? "");
  return "";
}

const isYes = (v) => {
  if (v === true || v === 1) return true;
  const s = String(v ?? "").trim().toLowerCase();
  return ["yes", "y", "1", "có", "co", "true"].includes(s);
};

const containsNeedPelmet = (s) =>
  String(s || "").toLowerCase().includes("pelmet");

const containsDuolite = (s) =>
  /\bduolite\b/i.test(String(s || ""));

const isUnequalLike = (s) => /\bunequal\b/i.test(String(s || ""));

const isPlasticBallRBB = (s) =>
  /Xích nhựa thanh đáy tròn \(plastic ball Round Bottom Bar\)/i.test(String(s || ""));
// ============================================================================
// UTILITY FUNCTIONS - END
// ============================================================================


// ============================================================================
// BUSINESS RULES CONSTANTS - START
// ============================================================================

// --- Prod 157 Wood / Faux Operation Sets ---
const PROD_157_WOOD_OPS = new Set([164, 158, 169, 161, 166, 162]);
const PROD_157_FAUX_OPS = new Set([165, 159, 168, 160, 167, 163]);

// --- Prod 157 Deco Tape Rule Sets (Ops 168, 160, 169, 161 -> Configs 3607, 3608, 3609, 3610) ---
const PROD_157_TAPE_OPS = new Set([168, 160, 169, 161]);
const PROD_157_TAPE_CONFIG_IDS = new Set([3607, 3608, 3609, 3610]);

// --- Prod 210 Curved Track Workmanship Rules (Ops 3658, 3659 -> Configs 3673, 3674) ---
const PROD_210_OPS = new Set([3658, 3659]);
const PROD_210_CURVED_CONFIG_IDS = new Set([3673, 3674]);
const PROD_210_OP_TO_CONFIGS = {
  3658: { curvedId: 3673, lengthId: 3669 },
  3659: { curvedId: 3674, lengthId: 3671 },
};

// --- Prod 157 2-on-1 Headrail (2 Panels, 15mm Gap) ---
const PROD_157_2ON1_OPS_MAP = {
  164: { left: 1726, right: 1727 },
  165: { left: 3639, right: 3640 },
  166: { left: 3641, right: 3642 },
  167: { left: 3643, right: 3644 },
  168: { left: 1728, right: 1729 },
  169: { left: 1730, right: 1731 },
};
const PROD_157_2ON1_OPS = new Set(Object.keys(PROD_157_2ON1_OPS_MAP).map(Number));
const PROD_157_2ON1_PAIRS = Object.values(PROD_157_2ON1_OPS_MAP);
const PROD_157_2ON1_CONFIG_IDS = new Set(
  PROD_157_2ON1_PAIRS.flatMap((p) => [p.left, p.right])
);
const PROD_157_GAP_MM = 15;
const PROD_157_MAX_PANEL_M2 = 2.9;
const PROD_157_MAX_TOTAL_M2 = 5.8;

// --- FST Rules ---
const PROD_305_CFG = new Set([2591, 2570, 2594, 2595, 2593]);
const PROD_306_CFG = new Set([2596, 2597, 2598]);
const PROD_493_CFG = new Set([1629, 1630]);
const PROD_1945_CFG = new Set([767, 1706, 1714, 3523]);
// [FIX] Define 2635 Config IDs
const PROD_2635_CFG = new Set([2649, 2650, 2651, 2652, 2653]);

// --- NEW: Prod 2181 & 2845 EXW Trigger Sets (Coupled/Motor logic) ---
const PROD_2181_CFG = new Set([2985, 2986, 2987, 2992, 2993, 2994]);
const PROD_2845_CFG = new Set([
  1915, 2269, 2280, 2853, 2886, 2888, 2889, 2892, 
  2937, 2939, 2944, 2945, 3029, 3030, 3031, 3032, 
  3033, 3035, 3036
]);

// --- LightLock Special Height Configs ---
const SPECIAL_HEIGHT_CONFIG_IDS = new Set([2426, 1755, 1771, 1992, 1998]);

// --- Pelmet Special Handling (prod_id = 213) ---
const PELMET_TRIGGER_TO_TARGET = { 2622: 2620, 2603: 2623, 2625: 2626, 2628: 2629 };
const PELMET_TRIGGERS = Object.keys(PELMET_TRIGGER_TO_TARGET).map((k) => Number(k));

// --- 2-on-1 Headrail Rule Sets Registry (Unified Option B) ---
const HEADRAIL_2ON1_REGISTRY = {
  2235: [
    { trigger: 1813, partner: 3432, targets: [3433, 3434] },
    { trigger: 2473, partner: 2688, targets: [254, 253] },
    { trigger: 2286, partner: 231, targets: [566, 255] },
  ],
  2236: [
    { trigger: 1813, partner: 1079, targets: [369, 345] },
    { trigger: 2286, partner: 232, targets: [234, 235] },
  ],
  1838: [
    { trigger: 1848, partner: 3368, targets: [3379, 3377] },
    { trigger: 1859, partner: 3382, targets: [3387, 3386] },
    { trigger: 1845, partner: 3437, targets: [3438, 3439] },
    { trigger: 1846, partner: 3390, targets: [3391, 3392] },
    { trigger: 1861, partner: 3417, targets: [3419, 3421] },
    { trigger: 1842, partner: 3398, targets: [3397, 3396] },
    { trigger: 1847, partner: 3402, targets: [3403, 3404] },
    { trigger: 1863, partner: 3425, targets: [3428, 3427] },
  ],
};
const RULE2235_SETS = HEADRAIL_2ON1_REGISTRY[2235];
const RULE2236_SETS = HEADRAIL_2ON1_REGISTRY[2236];
const RULE1838_SETS = HEADRAIL_2ON1_REGISTRY[1838];

// --- Prod 1323 Décor Tape Rules ---
const DECOR_STR = "Băng trang trí (Décor tape)";
const DECOR_PAIRS = { 2696: 2697, 2398: 2399, 2512: 2693 };
const DECOR_FOLLOWUPS = new Set([2697, 2399, 2693]);
const DECOR_OPS = new Set([1320, 631, 633]);

// --- Prod 2238 Height Rules ---
const PROD_2238_HEIGHT_TOKEN = ">2743 mm";
const PROD_2238_TRIGGER_TO_FOLLOW = { 1828: 1829, 1759: 1761, 1775: 1773, 2828: 2827 };
const PROD_2238_FOLLOWUPS = new Set(Object.values(PROD_2238_TRIGGER_TO_FOLLOW).map(Number));
const PROD_2238_RD_CONFIG_IDS = new Set([1766, 1830, 2705, 2734]);
// --- Prod 1910 RD Configs ---
const PROD_1910_RD_CONFIG_IDS = new Set([3285, 2278, 3292, 2277, 2276, 2272]);

// --- Side Alu CH Restrictions ---
const SIDE_ALU_CONFIG_IDS = new Set([1140, 1373, 1618, 1624, 2430, 3520]);
const SIDE_ALU_CH_TOKEN = "side alu ch";
const SIDE_ALU_CH_MAX_HEIGHT_MM = 6500;

// --- Prod 315 BO Fabric Rules ---
const PROD_315_BO_DISABLED_OPS = new Set([371, 374, 375, 553]);

// --- Prod 493 Mirror Rules ---
const OPS_493_MIRROR = new Set([570, 577, 583, 585, 588, 1676, 1677, 1678, 1679, 1680]);
const OPS_493_MARK = new Set([570, 577, 583, 585, 588]);

// --- Duolite RD Operations ---
const DUOLITE_RD_OPS = new Set([537, 543, 1814, 541, 1820, 545, 761]);

// --- Prod 2238 Duolite Gating ---
const CONFIG_2238_DUOLITE_TRIGGER_ID = 2807;
const CONFIG_2238_DUOLITE_GATED_DEFAULT_IDS = new Set([2385, 2389]);
const CONFIG_2238_DUOLITE_GATED_OPS_IDS = new Set([2812, 2813, 2814, 2815, 2816]);

// --- Quantity Label ---
const QTY_LABEL = "SL (Qty)";

// ============================================================================
// BUSINESS RULES CONSTANTS - END
// ============================================================================


// ============================================================================
// MAIN COMPONENT DEFINITION - START
// ============================================================================
export default function SizeCheckModal({
  open,
  onClose,
  width_mm,
  height_mm,
  prod_id,
  prod_label,
  operation_id,
  operation_label,
  selectedOpIds,
  limit_by_code,
}) {

  // ============================================================================
  // AUTHENTICATION & PURCHASER STATE - START
  // ============================================================================
  const [isPurchaser, setIsPurchaser] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/auth?action=me-user", {
          credentials: "include",
        });
        const j = await r.json().catch(() => ({}));
        if (alive) setIsPurchaser(!!j.purchaser);
      } catch {
        if (alive) setIsPurchaser(false);
      } finally {
        if (alive) setAuthChecked(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // NEW: Cart Modal State
  const [showCartModal, setShowCartModal] = useState(false);
  // --- NEW: Headless Cart Calculation State ---
  const [calculatingCart, setCalculatingCart] = useState(false);

  // NEW: Guide Modal State
  const [guideOpen, setGuideOpen] = useState(false);
  const activeGuide = HDSD_DATA["sizecheckmodal"] || HDSD_DATA.default;

  // ============================================================================
  // AUTHENTICATION & PURCHASER STATE - END
  // ============================================================================


  // ============================================================================
  // SPLIT PANELS STATE (UNIFIED 2-ON-1 REGISTRY) - START
  // ============================================================================
  const [splitPanelSizes, setSplitPanelSizes] = useState(null);
  // ============================================================================
  // SPLIT PANELS STATE (UNIFIED 2-ON-1 REGISTRY) - END
  // ============================================================================


  // ============================================================================
  // DEFAULT CONFIGURATION STATE (SQL #1) - START
  // ============================================================================
  const [rows, setRows] = useState([]); // [{config_id, config, options, def_value}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showRetailPrice, setShowRetailPrice] = useState(false);

  // Fabric collections
  const [fabricChoices, setFabricChoices] = useState([]); // [<collection name>, ...]
  const [fabricList, setFabricList] = useState([]); // [{ col_id: number, collection: string }]

  // Prod 157 Wood/Faux Color choices for config 2389
  const [prod157Colors, setProd157Colors] = useState([]);

  // Prod 209 Color choices for config 2389
  const [prod209Colors, setProd209Colors] = useState([]);
  const [prod209List, setProd209List] = useState([]); // [{ col_id, color, grp, price_grp_id }]

  // local selections for DEFAULT config
  const [values, setValues] = useState({}); // { [config_id]: string }
  // ============================================================================
  // DEFAULT CONFIGURATION STATE (SQL #1) - END
  // ============================================================================


  // ============================================================================
  // OPERATION-SPECIFIC CONFIG STATE (SQL #3) - START
  // ============================================================================
  const [rowsOps, setRowsOps] = useState([]); // same shape as rows
  const [loadingOps, setLoadingOps] = useState(false);
  const [errorOps, setErrorOps] = useState("");
  const [valuesOps, setValuesOps] = useState({}); // { [config_id]: string }

  // Duolite choices for ops section (2513/2514)
  const [duoliteChoices, setDuoliteChoices] = useState([]); // names only
  const [, setDuoliteList] = useState([]); // we only need the setter

  // Sheer fabric choices for ops config 2813
  const [sheerChoicesOps, setSheerChoicesOps] = useState([]);

  // RD fabric choices for ops config 2815
  const [rdChoicesOps2815, setRdChoicesOps2815] = useState([]);

  // ============================================================================
  // OPERATION-SPECIFIC CONFIG STATE (SQL #3) - END
  // ============================================================================


  // ============================================================================
  // MODAL & FST ORCHESTRATION STATE - START
  // ============================================================================
  // FST orchestration state
  const [showFstHeadless, setShowFstHeadless] = useState(false);
  const [fstPayload, setFstPayload] = useState(null); // { exwTransform, selectedOpIdsAdjusted }

  const [allowContinue, setAllowContinue] = useState(true);

  // prod 2238 Duolite hint: ensure alert only fires once per open
  const [hasShown2238DuoliteHint, setHasShown2238DuoliteHint] = useState(false);

// Coupled Warning State
  const [showCoupledWarning, setShowCoupledWarning] = useState(false);
  const [hasShownCoupledWarning, setHasShownCoupledWarning] = useState(false);  

  // [FIX] REMOVED CONFLICTING useMemo that referenced undefined components
  // HeadlessFstModal is already imported as a Lazy component at the top of the file.
  // ============================================================================
  // MODAL & FST ORCHESTRATION STATE - END
  // ============================================================================


  // ============================================================================
  // MEMOIZED COMPUTATIONS & DERIVED STATE - START
  // ============================================================================
  // form.* context
  const formCtx = useMemo(() => ({ prod_label }), [prod_label]);

  // which ops IDs are in play
  const opIdsForQuery = useMemo(() => {
    const arr = Array.isArray(selectedOpIds)
      ? selectedOpIds
      : operation_id
      ? [operation_id]
      : [];
    return arr
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n > 0);
  }, [selectedOpIds, operation_id]);

    // prod 2238 + op 1823: Duolite-gated ops configs
  const is2238WithOp1823 = useMemo(
    () =>
      Number(prod_id) === 2238 &&
      opIdsForQuery.some((id) => Number(id) === 1823),
    [prod_id, opIdsForQuery]
  );

  const duoliteSelectedFor2807 = useMemo(() => {
    if (!is2238WithOp1823) return false;
    const key = String(CONFIG_2238_DUOLITE_TRIGGER_ID);
    const v = valuesOps[key] ?? "";
    return containsDuolite(v);
  }, [is2238WithOp1823, valuesOps]);

  // Fabric mirroring: active for 1945 unconditionally, or 493 with target operations
const isProd493WithTargetOp = useMemo(() => {
  const pid = Number(prod_id);
  if (pid === 1945) return true;
  if (pid === 493) {
    return opIdsForQuery.some((id) => OPS_493_MIRROR.has(Number(id)));
  }
  return false;
}, [prod_id, opIdsForQuery]);

// prod 493: needs special fabric exw handling?
const needsFabricExwSpecial = useMemo(() => {
  if (Number(prod_id) !== 493) return false;
  const hasTargetOp = opIdsForQuery.some((id) => OPS_493_MARK.has(Number(id)));
  if (!hasTargetOp) return false;

  // check both default + ops maps, because those config_ids could appear in either set
  const pick = (id) => String((values && values[String(id)]) || (valuesOps && valuesOps[String(id)]) || "").trim();

  return (
    isPlasticBallRBB(pick(2386)) ||
    isPlasticBallRBB(pick(1614)) ||
    isPlasticBallRBB(pick(1327))
  );
}, [prod_id, opIdsForQuery, values, valuesOps]);

// Duolite RD op detection for 533 + 2238 + 1910
//   - 533: 537, 543
//   - 2238: 1814, 541, 543, 1820
//   - 1910: 1814, 545, 541, 543, 761
const DUOLITE_RD_OPS = new Set([537, 543, 1814, 541, 1820, 545, 761]);

const duoliteSelectedOp = useMemo(
  () => opIdsForQuery.find((id) => DUOLITE_RD_OPS.has(Number(id))),
  [opIdsForQuery]
);

const shouldUseDuSheerSQL = useMemo(() => {
  const pid = Number(prod_id);

  // Use Du-Sheer / Duolite Fabric Collection SQL *only* when
  // a Duolite RD op is actually selected:
  //   - 533 → ops 537 / 543
  //   - 2238 → ops 1814 / 541 / 543 / 1820
  //   - 1910 → ops 1814 / 545 / 541 / 543 / 761
  if (pid === 533 || pid === 2238 || pid === 1910) {
    return Number.isInteger(duoliteSelectedOp);
  }

  // All other products → legacy / normal fabric logic
  return false;
}, [prod_id, duoliteSelectedOp]);

  // Qty is taken from required config row "SL (Qty)"
  const QTY_LABEL = "SL (Qty)";
  const qty = useMemo(() => {
    const row = Array.isArray(rows)
      ? rows.find(
          (r) =>
            String(r?.config || "").trim().toLowerCase() ===
            QTY_LABEL.toLowerCase()
        )
      : null;
    const raw = row ? values?.[String(row.config_id)] : undefined;
    const n = parseFloat(String(raw ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [rows, values]);

  const selectedFabricName = useMemo(() => {
    if (Number(prod_id) === 209) {
      return String(values["2389"] ?? "");
    }
    return String(values["2385"] ?? "");
  }, [values, prod_id]);

  const fabricPriceGroupId = useMemo(() => {
    if (Number(prod_id) === 209) {
      const selColor = String(values["2389"] ?? "").trim().toLowerCase();
      if (!selColor) return undefined;
      const row = prod209List.find(
        (r) => String(r.color || "").trim().toLowerCase() === selColor
      );
      return row ? Number(row.price_grp_id || row.col_id) : undefined;
    }

    if (!selectedFabricName) return undefined;
    const row = fabricList.find(
      (r) =>
        String(r.collection || "")
          .trim()
          .toLowerCase() ===
        selectedFabricName.trim().toLowerCase()
    );
    return row ? Number(row.col_id) : undefined;
  }, [fabricList, selectedFabricName, prod_id, values, prod209List]);

  const isProd315BOFabric = useMemo(
    () =>
      Number(prod_id) === 315 &&
      /\bbo\b/i.test(String(selectedFabricName || "")),
    [prod_id, selectedFabricName]
  );

const specialYesSelected = useMemo(() => {
    const ids = Object.keys(valuesOps || {}).map((k) => Number(k));

    // Helper 1: Coupled/Motor Trigger (for 305, 306, 2635)
    const isCoupledTrigger = (v) => {
      if (isYes(v)) return true;
      const s = String(v || "").trim().toLowerCase();
      const hasKeyword = s.includes("coupled") || s.includes("couple") || s.includes("motor");
      const isNegative = s.includes("không") || s.includes("no ") || s.includes("no)");
      return hasKeyword && !isNegative;
    };

// Helper 2: EXW Surcharge Trigger (Specific per product)
    const isSpecialExwTrigger = (v, pid) => {
      const s = String(v || "").trim();
      
      if (Number(pid) === 2181) {
        // [2181] No Pelmet (Deduct) OR Need BO/White/Speed (Add)
        return s.includes("No need Pelmet 75") || 
               s.includes("No need Pelmet 95") ||
               s.includes("Need BO") || 
               s.includes("Need white") ||
               s.includes("Match blind speed");
      }
      
      if (Number(pid) === 2845) {
        // [2845] No Pelmet (Deduct) OR Match Pelmet/Speed OR Need RainOut/BO/White (Add)
        return s.includes("No need Pelmet 135") || 
               s.includes("No need Pelmet 165") || 
               s.includes("Match Pelmet") ||
               s.includes("Match blind speed") ||
               s.includes("Need RainOut") ||
               s.includes("Need BO") ||
               s.includes("Need white");
      }
      
      return false;
    };

    if (Number(prod_id) === 305)
      return ids.some((id) => PROD_305_CFG.has(id) && isCoupledTrigger(valuesOps[String(id)]));

    if (Number(prod_id) === 306)
      return ids.some((id) => PROD_306_CFG.has(id) && isCoupledTrigger(valuesOps[String(id)]));
      
    if (Number(prod_id) === 2635)
      return ids.some((id) => PROD_2635_CFG.has(id) && isCoupledTrigger(valuesOps[String(id)]));

    // [NEW] 2181 Trigger
    if (Number(prod_id) === 2181)
      return ids.some((id) => PROD_2181_CFG.has(id) && isSpecialExwTrigger(valuesOps[String(id)], 2181));

    // [NEW] 2845 Trigger
    if (Number(prod_id) === 2845)
      return ids.some((id) => PROD_2845_CFG.has(id) && isSpecialExwTrigger(valuesOps[String(id)], 2845));

    return false;
  }, [valuesOps, prod_id]);

// prod 493 / 1945 → double base EXW when specific config_ids contain "Coupled Motor"
const exwTransform493 = useMemo(() => {
  const pid = Number(prod_id);
  if (pid !== 493 && pid !== 1945) return null;

  // Pick the correct config set based on product
  const cfgSet = pid === 493 ? PROD_493_CFG : PROD_1945_CFG;

  const allVals = { ...(values || {}), ...(valuesOps || {}) };
  const ids = Object.keys(allVals).map((k) => Number(k));
  let hasCoupledMotor = false;

  for (const id of ids) {
    if (!cfgSet.has(id)) continue;

    const raw = String(allVals[String(id)] ?? "").trim().toLowerCase();
    const hasKeyword = raw.includes("coupled") || raw.includes("couple") || raw.includes("motor") || isYes(raw);
    const isNegative = raw.includes("không") || raw.includes("no ") || raw.includes("no)");

    if (hasKeyword && !isNegative) {
      hasCoupledMotor = true;
      break;
    }
  }

  if (!hasCoupledMotor) return null;

  // factor = 2, deductX = 0 → doubles base EXW in RetailModal
  return { factor: 2, deductX: 0 };
}, [prod_id, values, valuesOps]);

  const summaryExwTransform = useMemo(() => {
    if (fstPayload && fstPayload.exwTransform) {
      return fstPayload.exwTransform;
    }
    return exwTransform493;
  }, [fstPayload, exwTransform493]);
  // ============================================================================
  // MEMOIZED COMPUTATIONS & DERIVED STATE - END
  // ============================================================================


  // ============================================================================
  // BUSINESS RULES COMPUTATIONS (2235, 2236, 1323, 2238) - START
  // ============================================================================

  // --- Unified 2-on-1 Headrail Rule Computation ---
  const splitPanelRule = useMemo(() => {
    const out = {
      lockMap: {},
      errors: {},
      pairErrors: {},
      activeTargets: new Set(),
      panels: null,
    };

    const activeSets = HEADRAIL_2ON1_REGISTRY[Number(prod_id)];
    if (!activeSets || activeSets.length === 0) return out;

    const idsPresent = new Set(
      (rowsOps || []).map((r) => Number(r.config_id))
    );
    const W = toIntMm(width_mm);
    const H = toIntMm(height_mm);
    const halfA = W != null ? Math.floor(W / 2) : null;
    const halfB = W != null && halfA != null ? W - halfA : null;

    let panelsComputed = null;

    for (const set of activeSets) {
      const { partner, targets } = set;
      const [L, R] = targets.map(Number);

      // Require controlling partner dropdown to be present in operational configuration
      if (!idsPresent.has(partner)) continue;

      out.activeTargets.add(L);
      out.activeTargets.add(R);

      const partnerVal = String(valuesOps[String(partner)] ?? "");
      const unequal = isUnequalLike(partnerVal);

      if (!unequal) {
        if (halfA != null && halfB != null) {
          out.lockMap[String(L)] = {
            value: String(halfA),
            reason: "Tự động chia đôi theo chiều rộng",
          };
          out.lockMap[String(R)] = {
            value: String(halfB),
            reason: "Tự động chia đôi theo chiều rộng",
          };
          if (H != null) {
            panelsComputed = {
              left: {
                width_mm: halfA,
                height_mm: H,
                left_config_id: L,
              },
              right: {
                width_mm: halfB,
                height_mm: H,
                right_config_id: R,
              },
              meta: {
                set: String(partner),
                mode: "locked",
              },
            };
          }
        }
        continue;
      }

      const rawL = valuesOps[String(L)];
      const rawR = valuesOps[String(R)];
      const l = toIntMm(rawL);
      const r = toIntMm(rawR);
      const MAX = 2743;
      const key = `${L}-${R}`;

      if (rawL == null || rawL === "" || l == null) {
        out.errors[String(L)] = "Nhập số nguyên (mm).";
      } else if (l > MAX) {
        out.errors[String(L)] = `Không quá ${MAX} mm.`;
      }

      if (rawR == null || rawR === "" || r == null) {
        out.errors[String(R)] = "Nhập số nguyên (mm).";
      } else if (r > MAX) {
        out.errors[String(R)] = `Không quá ${MAX} mm.`;
      }

      if (l != null && r != null && l <= MAX && r <= MAX) {
        if (W != null && l + r !== W) {
          out.pairErrors[key] = `Tổng hai bên phải bằng ${W} mm.`;
        } else {
          const small = Math.min(l, r);
          const large = Math.max(l, r);
          if (small * 3 < large) {
            out.pairErrors[key] = "Bên nhỏ phải ≥ 1/3 bên lớn.";
          }
        }

        if (
          !out.pairErrors[key] &&
          Object.keys(out.errors).length === 0 &&
          H != null
        ) {
          panelsComputed = {
            left: {
              width_mm: l,
              height_mm: H,
              left_config_id: L,
            },
            right: {
              width_mm: r,
              height_mm: H,
              right_config_id: R,
            },
            meta: {
              set: String(partner),
              mode: "unequal",
            },
          };
        }
      }
    }

    out.panels = panelsComputed;
    return out;
  }, [prod_id, rowsOps, valuesOps, width_mm, height_mm]);

  // Backward compatibility references
  const rule2235Computed = splitPanelRule;
  const rule2235 = splitPanelRule;
  const rule2236 = splitPanelRule;

  // --- Prod 157 2-on-1 Headrail (15mm Gap, Max 2.9m2/panel, Max 5.8m2 total) ---
  const rule157_2on1 = useMemo(() => {
    const out = {
      is2on1: false,
      errors: {},
      pairErrors: {},
      totalError: null,
      panels: null,
    };

    if (Number(prod_id) !== 157) return out;

    // Discover active pair directly from loaded rowsOps or from opIdsForQuery
    const activePair = PROD_157_2ON1_PAIRS.find((p) =>
      (rowsOps || []).some((r) => Number(r.config_id) === p.left)
    ) || (() => {
      const activeOp = opIdsForQuery.find((id) => PROD_157_2ON1_OPS.has(Number(id)));
      return activeOp ? PROD_157_2ON1_OPS_MAP[Number(activeOp)] : null;
    })();

    if (!activePair) return out;

    out.is2on1 = true;
    const W = toIntMm(width_mm);
    const H = toIntMm(height_mm);

    if (W == null || H == null) return out;

    // 1. Total unit area verification (max 5.8 m2)
    const totalAreaM2 = (W * H) / 1000000;
    if (totalAreaM2 > PROD_157_MAX_TOTAL_M2) {
      out.totalError = `Tổng diện tích (${totalAreaM2.toFixed(2)} m²) vượt quá giới hạn cho phép ${PROD_157_MAX_TOTAL_M2} m².`;
    }

    const netWidth = W - PROD_157_GAP_MM;
    if (netWidth <= 0) {
      out.totalError = `Chiều rộng phải lớn hơn khoảng hở giữa 2 cánh (${PROD_157_GAP_MM} mm).`;
      return out;
    }

    const defaultL = Math.floor(netWidth / 2);
    const defaultR = netWidth - defaultL;

    // Validate the active pair for this operation
    const L_id = String(activePair.left);
    const R_id = String(activePair.right);

    const rawL = valuesOps[L_id];
    const rawR = valuesOps[R_id];

    // Fall back to balanced default if unpopulated or reset to "0"
    const effectiveL = (rawL != null && rawL !== "" && rawL !== "0") ? rawL : String(defaultL);
    const effectiveR = (rawR != null && rawR !== "" && rawR !== "0") ? rawR : String(defaultR);
    const l = toIntMm(effectiveL);
    const r = toIntMm(effectiveR);
    const pairKey = `${activePair.left}-${activePair.right}`;

    if (l == null || l <= 0) {
      out.errors[L_id] = "Kích thước cánh trái phải lớn hơn 0 mm.";
    } else {
      const areaL = (l * H) / 1000000;
      if (areaL > PROD_157_MAX_PANEL_M2) {
        out.errors[L_id] = `Diện tích cánh trái (${areaL.toFixed(2)} m²) vượt quá ${PROD_157_MAX_PANEL_M2} m².`;
      }
    }

    if (r == null || r <= 0) {
      out.errors[R_id] = "Kích thước cánh phải phải lớn hơn 0 mm.";
    } else {
      const areaR = (r * H) / 1000000;
      if (areaR > PROD_157_MAX_PANEL_M2) {
        out.errors[R_id] = `Diện tích cánh phải (${areaR.toFixed(2)} m²) vượt quá ${PROD_157_MAX_PANEL_M2} m².`;
      }
    }

    if (l != null && r != null && l > 0 && r > 0) {
      if (l + r !== netWidth) {
        out.pairErrors[pairKey] = `Tổng 2 cánh (${l + r} mm) cộng khoảng hở ${PROD_157_GAP_MM} mm phải bằng ${W} mm (Tổng 2 cánh phải bằng ${netWidth} mm).`;
      }
    }

    return out;
  }, [prod_id, opIdsForQuery, width_mm, height_mm, valuesOps]);

  // --- Prod 1323 Décor Tape Rules ---
  const decorEnabled = useMemo(() => {
    const map = { 2697: false, 2399: false, 2693: false };
    if (Number(prod_id) !== 1323) return map;

    const hasTargetOp = opIdsForQuery.some((id) => DECOR_OPS.has(Number(id)));
    if (!hasTargetOp) return map;

    for (const [primaryStr, followStr] of Object.entries(DECOR_PAIRS)) {
      const primary = Number(primaryStr);
      const follow = Number(followStr);
      const val = String(valuesOps[String(primary)] ?? "");
      if (val === DECOR_STR) map[follow] = true;
    }
    return map;
  }, [prod_id, opIdsForQuery, valuesOps]);

  // --- Prod 2238 Height Rules ---
  const prod2238FollowEnabled = useMemo(() => {
    const map = { 1829: false, 1761: false, 1773: false, 2827: false };
    if (Number(prod_id) !== 2238) return map;

    for (const [triggerStr, followStr] of Object.entries(PROD_2238_TRIGGER_TO_FOLLOW)) {
      const triggerId = Number(triggerStr);
      const followId = Number(followStr);
      const selected = String(valuesOps[String(triggerId)] ?? "");
      if (selected.includes(PROD_2238_HEIGHT_TOKEN)) {
        map[followId] = true;
      }
    }

    return map;
  }, [prod_id, valuesOps]);
  // ============================================================================
  // BUSINESS RULES COMPUTATIONS (2235, 2236, 1323, 2238) - END
  // ============================================================================


  // ============================================================================
  // VALIDATION LOGIC - START
  // ============================================================================
  const allValidDefault = useMemo(() => {
    if (!rows || rows.length === 0) return false;

    // prod 2238 + op 1823 + Duolite on 2807 → 2385/2389 are hidden/gated
    const duoliteGatingActive =
      is2238WithOp1823 && duoliteSelectedFor2807;

    for (const r of rows) {
      const idStr = String(r.config_id);
      const idNum = Number(r.config_id);

      // When gating is active, treat 2385 / 2389 as disabled (not required)
      if (
        duoliteGatingActive &&
        CONFIG_2238_DUOLITE_GATED_DEFAULT_IDS.has(idNum)
      ) {
        continue;
      }

      const v = String(values[idStr] ?? "").trim();
      if (!v) return false;

      const opts = String(r.options || "");
      if (isDigitInput(opts) && !isValid2dpNumber(v)) return false;
    }

    // SHW requires valid fabric choice at 2385
    // but ONLY when Duolite gating is NOT active
    if (
      !duoliteGatingActive &&
      String(limit_by_code || "").toUpperCase() === "SHW"
    ) {
      const has2385 = rows.some(
        (r) => Number(r.config_id) === 2385
      );
      if (has2385) {
        const selected = String(values["2385"] ?? "").trim();
        if (
          !selected ||
          !fabricChoices.includes(selected)
        ) {
          return false;
        }
      }
    }

    // Prod 157 requires a valid selection from prod157Colors for config 2389
    if (Number(prod_id) === 157) {
      const selectedColor = String(values["2389"] ?? "").trim();
      if (!selectedColor) return false;
      if (prod157Colors.length > 0 && !prod157Colors.includes(selectedColor)) {
        return false;
      }
    }

    // Prod 209 requires a valid selection from prod209Colors for config 2389
    if (Number(prod_id) === 209) {
      const selectedColor = String(values["2389"] ?? "").trim();
      if (!selectedColor) return false;
      if (prod209Colors.length > 0 && !prod209Colors.includes(selectedColor)) {
        return false;
      }
    }

    return true;
  }, [
    rows,
    values,
    limit_by_code,
    fabricChoices,
    is2238WithOp1823,
    duoliteSelectedFor2807,
  ]);

  // Ops validity, including Duolite and 2235 Unequal rules
  const allValidOps = useMemo(() => {
    if (!rowsOps || rowsOps.length === 0) return true;

    // numeric validation for ops
    for (const r of rowsOps) {
      const idStr = String(r.config_id);
      const v = String(valuesOps[idStr] ?? "").trim();
      const opts = String(r.options || "");
      if (
        isDigitInput(opts) &&
        v &&
        !isValid2dpNumber(v)
      )
        return false;
    }

    const present = (id) =>
      rowsOps.some((r) => Number(r.config_id) === id);
    const get = (id) =>
      String(valuesOps[String(id)] ?? "").trim();

// Duolite mandatory when present
const duoliteIds = [2513, 2514];
if (Number(prod_id) === 2238) {
  for (const cid of PROD_2238_RD_CONFIG_IDS) {
    duoliteIds.push(cid);
  }
}
if (Number(prod_id) === 1910) {
  for (const cid of PROD_1910_RD_CONFIG_IDS) {
    duoliteIds.push(cid);
  }
}

for (const id of duoliteIds) {
  if (present(id)) {
    const v = get(id);
    if (!v) return false;
    if (
      duoliteChoices.length > 0 &&
      !duoliteChoices.includes(v)
    ) {
      return false;
    }
  }
}

    // --- prod 2238 + op 1823 + Duolite on 2807:
    //     require 2813, 2814, 2815, 2816 to be non-empty ---
    if (is2238WithOp1823 && duoliteSelectedFor2807) {
      const requiredOps = [2813, 2814, 2815, 2816];
      for (const cid of requiredOps) {
        if (present(cid)) {
          const v = get(cid);
          if (!v) return false;
        }
      }
    }

    // Required text inputs (2378, 2515)
    for (const id of [2378, 2515]) {
      if (present(id)) {
        if (!get(id)) return false;
      }
    }

    // Unified 2-on-1 Headrail pair validation
    if (HEADRAIL_2ON1_REGISTRY[Number(prod_id)]) {
      if (Object.keys(splitPanelRule.errors).length > 0)
        return false;
      if (Object.keys(splitPanelRule.pairErrors).length > 0)
        return false;
    }

    // Prod 157 2-on-1 Validation
    if (Number(prod_id) === 157 && rule157_2on1.is2on1) {
      if (rule157_2on1.totalError) return false;
      if (Object.keys(rule157_2on1.errors).length > 0) return false;
      if (Object.keys(rule157_2on1.pairErrors).length > 0) return false;
    }

    // --- prod 1323 Décor follow-up required when enabled ---
if (Number(prod_id) === 1323) {
  // only enforce when at least one targeted op is active
  const hasTargetOp = opIdsForQuery.some((id) => DECOR_OPS.has(Number(id)));
  if (hasTargetOp) {
    for (const follow of DECOR_FOLLOWUPS) {
      if (decorEnabled[follow]) {
        if (!get(follow)) return false; // must be non-empty when enabled
      }
    }
  }
}

    // --- prod 2238 '>2743 mm' follow-ups required + min length (>127) ---
    if (Number(prod_id) === 2238) {
      for (const [triggerStr, followStr] of Object.entries(PROD_2238_TRIGGER_TO_FOLLOW)) {
        const triggerId = Number(triggerStr);
        const followId = Number(followStr);

        // only care if the trigger row is present in rowsOps
        if (!present(triggerId)) continue;

        const triggerVal = get(triggerId);
        const followVal = get(followId);

        const enabled =
          triggerVal &&
          triggerVal.includes(PROD_2238_HEIGHT_TOKEN);

        if (enabled) {
          // must be non-empty AND > 127
          if (!followVal) return false;

          const num = Number(followVal);
          if (!Number.isFinite(num) || num <= 127) {
            return false;
          }
        }
      }
    }

    return true;
  }, [
  rowsOps,
  valuesOps,
  duoliteChoices,
  prod_id,
  splitPanelRule.errors,
  splitPanelRule.pairErrors,
  decorEnabled,
  opIdsForQuery,
  is2238WithOp1823,
  duoliteSelectedFor2807,
  ]);

  const allValid = allValidDefault && allValidOps;
  // ============================================================================
  // VALIDATION LOGIC - END
  // ============================================================================

  // ============================================================================
  // DATA FETCHING EFFECTS - START
  // ============================================================================

  // --- Reset on Close ---
  useEffect(() => {
    if (!open) {
      setValuesOps({});
      setShowFstHeadless(false);
      setFstPayload(null);
      setHasShown2238DuoliteHint(false);
      // [ADDED] Reset coupled warning history
      setShowCoupledWarning(false);
      setHasShownCoupledWarning(false);
    }
  }, [open]);

  // --- Default Config Fetch (SQL #1) ---
  useEffect(() => {
    let ignore = false;
    async function loadList() {
      setLoading(true);
      setError("");
      try {
        const resp = await fetch(
          `/api/default-product-config?action=list`,
          { method: "GET" }
        );
        const data = await resp.json();
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }
        if (ignore) return;

        let items = Array.isArray(data.items) ? data.items : [];

        // HW/SHW gating for Fabric Collection (2385) - Allowed for SHW or custom Exception Product 199
        const isSHW = String(limit_by_code || "").toUpperCase() === "SHW";
        if (!isSHW && Number(prod_id) !== 199) {
          items = items.filter((r) => Number(r.config_id) !== 2385);
        }
        
        // --- prod 1323 / 199 / 493: hide config 2389 from the UI (but keep it in state) ---
        const originalItems = items; // keep a copy for seeding (includes 2389)
        if (Number(prod_id) === 1323 || Number(prod_id) === 199 || isProd493WithTargetOp) {
          items = items.filter((r) => Number(r.config_id) !== 2389);
        }

        setRows(items); // IMPORTANT: set the filtered list once

        // Seed defaults (use originalItems so hidden 2389 still gets a value)
        const seed = {};
        for (const row of originalItems) {
          const id = String(row.config_id);
          const opts = String(row.options || "");
          const def = row.def_value != null ? String(row.def_value) : "";

          if (isFormToken(opts)) {
            seed[id] = valueFromFormToken(opts, formCtx);
          } else if (isTextInput(opts)) {
            seed[id] = def || "";
          } else if (isDigitInput(opts)) {
            seed[id] = def && isValid2dpNumber(def) ? def : "";
          } else {
            const choices = splitChoices(opts);
            seed[id] = choices.includes(def) ? def : "";
          }
        }

        // --- prod 1323 / 199 / 493: at seed time, mirror 2389 to 2385 too ---
        if (Number(prod_id) === 1323 || Number(prod_id) === 199 || isProd493WithTargetOp) {
          seed["2389"] = String(seed["2385"] ?? "");
        }

        // Prefetch fabric collections only if 2385 is present (SHW scenario or custom Exception Product 199)
        if (
          (isSHW || Number(prod_id) === 199) &&
          items.some((r) => Number(r.config_id) === 2385) &&
          Number(prod_id)
        ) {
          let names = [];
          let list = [];

if (shouldUseDuSheerSQL) {
  const params = new URLSearchParams({
    scope: "default_product_config",
    action: "du_sheer_fab_col",
    prod_id: String(prod_id),
    width: String(width_mm),
    height: String(height_mm),
    debug: "1",
  });

  // For 533, 2238, and 1910, pass the Duolite RD op as selectedopsid
  if (
    (Number(prod_id) === 533 || Number(prod_id) === 2238 || Number(prod_id) === 1910) &&
    Number.isInteger(duoliteSelectedOp)
  ) {
    params.set("selectedopsid", String(duoliteSelectedOp));
  }

  const u = `/api/data?${params.toString()}`;
  const r2 = await fetch(u, { method: "GET" });
  const d2 = await r2.json();
  if (!r2.ok || d2?.ok === false) {
    throw new Error(d2?.error || `HTTP ${r2.status}`);
  }
  const arr = Array.isArray(d2.items) ? d2.items : [];
  names = arr.map((x) => String(x.collection || ""));
  list = arr.map((x) => ({
    col_id: Number(x.col_id),
    collection: String(x.collection || ""),
  }));
} else {
    // Legacy SHW fabric collection path / Custom Product 199 exception route
    const params = new URLSearchParams();
    if (Number(prod_id) === 199) {
      params.set("scope", "default_product_config");
      params.set("action", "prod199_colors");
    } else {
      params.set("action", "fabric");
      params.set("prod_id", String(prod_id));
      params.set("width", String(width_mm));
      params.set("height", String(height_mm));
      params.set("selectedOpIds", JSON.stringify(opIdsForQuery));
    }
    const u = Number(prod_id) === 199 ? `/api/data?${params.toString()}` : `/api/default-product-config?${params.toString()}`;

    const r2 = await fetch(u, { method: "GET" });
    const d2 = await r2.json();
    if (!r2.ok || d2?.ok === false) {
      throw new Error(d2?.error || `HTTP ${r2.status}`);
    }
    const arr = Array.isArray(d2.items) ? d2.items : [];
    names = arr.map((x) => String(x.collection || ""));
    list = arr.map((x) => ({
      col_id: Number(x.col_id),
      collection: String(x.collection || ""),
    }));
}

          const v2385 = String(seed["2385"] ?? "");
          if (v2385 && !names.includes(v2385)) {
            seed["2385"] = "";
          }

          if (!ignore) {
            setFabricChoices(names);
            setFabricList(list);
          }
        } else {
          if (!ignore) {
            setFabricChoices([]);
            setFabricList([]);
          }
        }

        if (!ignore) setValues(seed);
      } catch (err) {
        if (!ignore) {
          setError(err?.message || "Load failed");
          setRows([]);
          setValues({});
          setFabricChoices([]);
          setFabricList([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    if (open) loadList();
    return () => {
      ignore = true;
    };
  }, [
    open,
    prod_id,
    formCtx,
    limit_by_code,
    width_mm,
    height_mm,
    opIdsForQuery,
    shouldUseDuSheerSQL, duoliteSelectedOp, isProd493WithTargetOp,
  ]);

  // --- Prod 157 Color Fetch (SQL #2-Custom for 157) ---
  useEffect(() => {
    let ignore = false;
    async function loadProd157Colors() {
      if (!open || Number(prod_id) !== 157) {
        if (!ignore) setProd157Colors([]);
        return;
      }

      const activeOpId = opIdsForQuery.length > 0 ? opIdsForQuery[0] : Number(operation_id);
      const isWood = PROD_157_WOOD_OPS.has(activeOpId);
      const isFaux = PROD_157_FAUX_OPS.has(activeOpId);

      if (!isWood && !isFaux) {
        if (!ignore) setProd157Colors([]);
        return;
      }

      try {
        const woodType = isWood ? "Wood" : "Faux";
        const params = new URLSearchParams({
          scope: "default_product_config",
          action: "prod157_colors",
          prod_id: "157",
          op_id: String(activeOpId),
          wood_type: woodType,
        });

        const resp = await fetch(`/api/data?${params.toString()}`);
        const data = await resp.json();
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }

        if (!ignore) {
          const items = Array.isArray(data.items) ? data.items : [];
          const colorNames = items
            .map((x) => String(x.color || "").trim())
            .filter(Boolean);
          setProd157Colors(colorNames);

          // Reconcile current selection if it is no longer in the valid options list
          setValues((prev) => {
            const cur = String(prev["2389"] ?? "");
            if (cur && !colorNames.includes(cur)) {
              return { ...prev, "2389": "" };
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("Failed to load Prod 157 colors:", err);
        if (!ignore) setProd157Colors([]);
      }
    }

    loadProd157Colors();
    return () => {
      ignore = true;
    };
  }, [open, prod_id, opIdsForQuery, operation_id]);

  // --- Prod 209 Color Fetch (SQL #2-Custom for 209) ---
  useEffect(() => {
    let ignore = false;
    async function loadProd209Colors() {
      if (!open || Number(prod_id) !== 209) {
        if (!ignore) setProd209Colors([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          scope: "default_product_config",
          action: "prod209_colors",
        });

        const resp = await fetch(`/api/data?${params.toString()}`);
        const data = await resp.json();
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }

        if (!ignore) {
          const items = Array.isArray(data.items) ? data.items : [];
          setProd209List(items);
          const colorNames = items
            .map((x) => String(x.color || "").trim())
            .filter(Boolean);
          setProd209Colors(colorNames);

          // Reconcile current selection if it is no longer in the valid options list
          setValues((prev) => {
            const cur = String(prev["2389"] ?? "");
            if (cur && !colorNames.includes(cur)) {
              return { ...prev, "2389": "" };
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("Failed to load Prod 209 colors:", err);
        if (!ignore) {
          setProd209Colors([]);
          setProd209List([]);
        }
      }
    }

    loadProd209Colors();
    return () => {
      ignore = true;
    };
  }, [open, prod_id]);

  // --- Prod 157 Deco Tape Default Sync (Configs 3607, 3608, 3609, 3610 based on Color 2389) ---
  useEffect(() => {
    let ignore = false;

    async function syncProd157DefaultTape() {
      if (!open || Number(prod_id) !== 157) return;

      const activeOpId = opIdsForQuery.length > 0 ? opIdsForQuery[0] : Number(operation_id);
      if (!PROD_157_TAPE_OPS.has(activeOpId)) return;

      const activeTapeRow = (rowsOps || []).find((r) =>
        PROD_157_TAPE_CONFIG_IDS.has(Number(r.config_id))
      );
      if (!activeTapeRow) return;

      const colorVal = String(values["2389"] ?? "").trim();
      if (!colorVal) return;

      try {
        const params = new URLSearchParams({
          scope: "default_product_config",
          action: "prod157_default_tape",
          prod_id: "157",
          color: colorVal,
        });

        const resp = await fetch(`/api/data?${params.toString()}`);
        const data = await resp.json();
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }

        if (!ignore && data.def_tape) {
          const tapeConfigIdStr = String(activeTapeRow.config_id);
          const targetTape = String(data.def_tape).trim();
          const choices = splitChoices(String(activeTapeRow.options || ""));

          if (choices.includes(targetTape) || !choices.length) {
            setValuesOps((prev) => {
              if (prev[tapeConfigIdStr] === targetTape) return prev;
              return {
                ...prev,
                [tapeConfigIdStr]: targetTape,
              };
            });
          }
        }
      } catch (err) {
        console.error("Failed to fetch Prod 157 default tape:", err);
      }
    }

    syncProd157DefaultTape();
    return () => {
      ignore = true;
    };
  }, [open, prod_id, opIdsForQuery, operation_id, rowsOps, values["2389"]]);

  // --- Operation Config Fetch (SQL #3) ---
  useEffect(() => {
    let ignore = false;

    async function loadOpsConfig() {
      if (!Number(prod_id) || opIdsForQuery.length === 0) {
        if (!ignore) {
          setRowsOps([]);
          setValuesOps({});
          setErrorOps("");
          setDuoliteChoices([]);
          setDuoliteList([]);
          setSheerChoicesOps([]);
          setRdChoicesOps2815([]);
        }
        return;
      }

      setLoadingOps(true);
      setErrorOps("");
      try {
        const params = new URLSearchParams({
          action: "ops_config",
          prod_id: String(prod_id),
          op_ids: JSON.stringify(opIdsForQuery),
        });
        const url = `/api/default-product-config?${params.toString()}`;
        const resp = await fetch(url, { method: "GET" });
        const data = await resp.json();
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }
        if (ignore) return;

        const items = Array.isArray(data.items) ? data.items : [];
        setRowsOps(items);

        // Seed valuesOps
        const seed = {};
        for (const row of items) {
          const id = String(row.config_id);
          const opts = String(row.options || "");
          const def = row.def_value != null ? String(row.def_value) : "";

          if (isFormToken(opts)) {
            seed[id] = valueFromFormToken(opts, formCtx);
          } else if (isTextInput(opts)) {
            seed[id] = def || "";
          } else if (isDigitInput(opts)) {
            seed[id] = def && isValid2dpNumber(def) ? def : "";
          } else {
            const choices = splitChoices(opts);
            seed[id] = choices.includes(def) ? def : "";
          }
        }

        // LightLock special: show height_mm in a read-only text input (config_id 2426)
        for (const row of items) {
          const cid = Number(row.config_id);
          if (SPECIAL_HEIGHT_CONFIG_IDS.has(cid)) {
            seed[String(cid)] = String(height_mm ?? "");
          }
        }

        // Prod 157: Pre-seed balanced 2-on-1 panel widths directly from items
        if (Number(prod_id) === 157) {
          const W = toIntMm(width_mm);
          if (W != null && W > PROD_157_GAP_MM) {
            const netW = W - PROD_157_GAP_MM;
            const leftW = Math.floor(netW / 2);
            const rightW = netW - leftW;
            for (const pair of PROD_157_2ON1_PAIRS) {
              if (items.some((r) => Number(r.config_id) === pair.left)) {
                seed[String(pair.left)] = String(leftW);
              }
              if (items.some((r) => Number(r.config_id) === pair.right)) {
                seed[String(pair.right)] = String(rightW);
              }
            }
          }
        }

        setValuesOps(seed);

        // Duolite list if Duolite RD configs present
        const needDuolite = items.some((r) => {
          const cid = Number(r.config_id);

          // Original Duolite RD (prod 533, configs 2513/2514)
          if ([2513, 2514].includes(cid)) return true;

          // New: prod 2238 RD configs
          if (Number(prod_id) === 2238 && PROD_2238_RD_CONFIG_IDS.has(cid)) return true;

          // Prod 1910 RD configs
          if (Number(prod_id) === 1910 && PROD_1910_RD_CONFIG_IDS.has(cid)) return true;

          return false;
        });

        if (needDuolite) {
          try {
            const p2 = new URLSearchParams({
              action: "ops_rd_duolite",
              prod_id: String(prod_id),
              width: String(width_mm),
              height: String(height_mm),
              selectedOpIds: JSON.stringify(opIdsForQuery),
            });
            const u2 = `/api/default-product-config?${p2.toString()}`;
            const r2 = await fetch(u2, { method: "GET" });
            const d2 = await r2.json();
            if (!r2.ok || d2?.ok === false) {
              throw new Error(d2?.error || `HTTP ${r2.status}`);
            }
            const arr = Array.isArray(d2.items) ? d2.items : [];
            const names = arr.map((x) => String(x.collection || ""));
            const list = arr.map((x) => ({
              col_id: Number(x.col_id),
              collection: String(x.collection || ""),
            }));

            if (!ignore) {
              setDuoliteChoices(names);
              setDuoliteList(list);

              const idsForDuolite = ["2513", "2514"];

              // Also include 2238 RD configs when prod_id is 2238
              if (Number(prod_id) === 2238) {
                for (const cid of PROD_2238_RD_CONFIG_IDS) {
                  idsForDuolite.push(String(cid));
                }
              }

              // Also include 1910 RD configs when prod_id is 1910
              if (Number(prod_id) === 1910) {
                for (const cid of PROD_1910_RD_CONFIG_IDS) {
                  idsForDuolite.push(String(cid));
                }
              }

              for (const id of idsForDuolite) {
                if (seed[id] && !names.includes(seed[id])) seed[id] = "";
              }

              setValuesOps((prev) => ({ ...seed, ...prev }));
            }

          } catch (e) {
            if (!ignore)
              setErrorOps((s) => s || e?.message || "Load Duolite failed");
          }
        } else {
          if (!ignore) {
            setDuoliteChoices([]);
            setDuoliteList([]);
          }
        }

        // SHEER fabric collection for ops config 2813
        const hasSheer2813 = items.some((r) => Number(r.config_id) === 2813);

        if (hasSheer2813) {
          try {
            const p3 = new URLSearchParams({
              scope: "default_product_config",
              action: "du_sheer_fab_col",
              prod_id: String(prod_id),
              width: String(width_mm),
              height: String(height_mm),
            });

            // Let backend infer selected operation from opIdsForQuery
            if (opIdsForQuery && opIdsForQuery.length > 0) {
              p3.set("selectedOpIds", JSON.stringify(opIdsForQuery));
            }

            const u3 = `/api/data?${p3.toString()}`;
            const r3 = await fetch(u3, { method: "GET" });
            const d3 = await r3.json();
            if (!r3.ok || d3?.ok === false) {
              throw new Error(d3?.error || `HTTP ${r3.status}`);
            }

            const arr3 = Array.isArray(d3.items) ? d3.items : [];
            const names3 = arr3.map((x) => String(x.collection || ""));

            if (!ignore) {
              setSheerChoicesOps(names3);
            }
          } catch (e) {
            if (!ignore) {
              setErrorOps((s) => s || e?.message || "Load SHEER fabric failed");
              setSheerChoicesOps([]);
            }
          }
        } else {
          if (!ignore) {
            setSheerChoicesOps([]);
          }
        }

        // RD fabric collection for ops config 2815 (standalone)
        const hasRd2815 = items.some((r) => Number(r.config_id) === 2815);

        if (hasRd2815) {
          try {
            const p4 = new URLSearchParams({
              action: "ops_rd_duolite",
              prod_id: String(prod_id),
              width: String(width_mm),
              height: String(height_mm),
              selectedOpIds: JSON.stringify(opIdsForQuery),
            });

            const u4 = `/api/default-product-config?${p4.toString()}`;
            const r4 = await fetch(u4, { method: "GET" });
            const d4 = await r4.json();
            if (!r4.ok || d4?.ok === false) {
              throw new Error(d4?.error || `HTTP ${d4.status}`);
            }

            const arr4 = Array.isArray(d4.items) ? d4.items : [];
            const names4 = arr4.map((x) => String(x.collection || ""));

            if (!ignore) {
              setRdChoicesOps2815(names4);
            }
          } catch (e) {
            if (!ignore) {
              setErrorOps((s) => s || e?.message || "Load RD fabric failed");
              setRdChoicesOps2815([]);
            }
          }
        } else {
          if (!ignore) {
            setRdChoicesOps2815([]);
          }
        }

      } catch (err) {
        if (!ignore) {
          setErrorOps(err?.message || "Load failed");
          setRowsOps([]);
          setValuesOps({});
          setDuoliteChoices([]);
          setDuoliteList([]);
          setSheerChoicesOps([]);
          setRdChoicesOps2815([]);
        }
      } finally {
        if (!ignore) setLoadingOps(false);
      }
    }

    loadOpsConfig();
    return () => {
      ignore = true;
    };
  }, [open, prod_id, opIdsForQuery, formCtx, width_mm, height_mm]);

  // --- Prod 157 2-on-1 Auto-Default Panel Widths: (Width - 15mm) / 2 ---
  useEffect(() => {
    if (!open || Number(prod_id) !== 157) return;

    const W = toIntMm(width_mm);
    if (W == null || W <= PROD_157_GAP_MM) return;

    const netWidth = W - PROD_157_GAP_MM;
    const defaultLeft = Math.floor(netWidth / 2);
    const defaultRight = netWidth - defaultLeft;

    // Detect active pair using loaded rowsOps first, then opIdsForQuery fallback
    const pair = PROD_157_2ON1_PAIRS.find((p) =>
      (rowsOps || []).some((r) => Number(r.config_id) === p.left)
    ) || (() => {
      const activeOp = opIdsForQuery.find((id) => PROD_157_2ON1_OPS.has(Number(id)));
      return activeOp ? PROD_157_2ON1_OPS_MAP[Number(activeOp)] : null;
    })();

    if (!pair) return;

    setValuesOps((prev) => {
      const leftKey = String(pair.left);
      const rightKey = String(pair.right);

      const curL = toIntMm(prev[leftKey]);
      const curR = toIntMm(prev[rightKey]);

      if (curL == null || curL <= 0 || curR == null || curR <= 0 || (curL + curR !== netWidth)) {
        if (prev[leftKey] !== String(defaultLeft) || prev[rightKey] !== String(defaultRight)) {
          return {
            ...prev,
            [leftKey]: String(defaultLeft),
            [rightKey]: String(defaultRight),
          };
        }
      }
      return prev;
    });
  }, [open, prod_id, opIdsForQuery, width_mm, rowsOps]);

  // --- Prod 210 Curved Track Workmanship Surcharge Sync ---
  useEffect(() => {
    let ignore = false;

    async function syncProd210CurvedSurcharge() {
      if (!open || Number(prod_id) !== 210) return;

      const activeOpId = opIdsForQuery.find((id) => PROD_210_OPS.has(Number(id)));
      if (!activeOpId || !PROD_210_OP_TO_CONFIGS[activeOpId]) return;

      const { curvedId, lengthId } = PROD_210_OP_TO_CONFIGS[activeOpId];
      const curvedKey = String(curvedId);
      const lengthKey = String(lengthId);

      const rawLength = valuesOps[lengthKey];
      const lengthMm = Number(rawLength);

      if (!Number.isFinite(lengthMm) || lengthMm <= 0) {
        if (!ignore) {
          setValuesOps((prev) => {
            if (prev[curvedKey] === "0") return prev;
            return { ...prev, [curvedKey]: "0" };
          });
        }
        return;
      }

      try {
        const params = new URLSearchParams({
          scope: "default_product_config",
          action: "curved_track_surcharge",
          prod_id: "210",
          length_mm: String(lengthMm),
        });

        const resp = await fetch(`/api/data?${params.toString()}`);
        const data = await resp.json();
        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }

        if (!ignore && data.surcharge != null) {
          const finalVal = String(data.surcharge);
          setValuesOps((prev) => {
            if (prev[curvedKey] === finalVal) return prev;
            return {
              ...prev,
              [curvedKey]: finalVal,
            };
          });
        }
      } catch (err) {
        console.error("Failed to calculate Prod 210 curved track surcharge:", err);
      }
    }

    syncProd210CurvedSurcharge();
    return () => {
      ignore = true;
    };
  }, [open, prod_id, opIdsForQuery, valuesOps["3669"], valuesOps["3671"]]);
  // ============================================================================
  // DATA FETCHING EFFECTS - END
  // ============================================================================


  // ============================================================================
  // BUSINESS RULES SYNC EFFECTS - START
  // ============================================================================

  // --- Prod 1323 / 199 / 493 Mirror Effect ---
  useEffect(() => {
    if (!open) return;
    const shouldMirror = Number(prod_id) === 1323 || Number(prod_id) === 199 || isProd493WithTargetOp;
    if (!shouldMirror) return;

    const v2385 = String(values["2385"] ?? "");
    setValues((prev) => {
      const cur = String(prev["2389"] ?? "");
      if (cur === v2385) return prev;
      return { ...prev, "2389": v2385 };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prod_id, isProd493WithTargetOp, values["2385"]]);

  // --- Decor Tape Clear Effect ---
  useEffect(() => {
    if (!open) return;
    if (Number(prod_id) !== 1323) return;

    setValuesOps((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const follow of DECOR_FOLLOWUPS) {
        const idStr = String(follow);
        const enabled = !!decorEnabled[follow];
        if (!enabled && next[idStr]) {
          next[idStr] = "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, prod_id, decorEnabled]);

  // --- Prod 2238 Clear Follow-ups Effect ---
  useEffect(() => {
    if (!open) return;
    if (Number(prod_id) !== 2238) return;

    setValuesOps((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const follow of PROD_2238_FOLLOWUPS) {
        const idStr = String(follow);
        const enabled = !!prod2238FollowEnabled[follow];
        if (!enabled && next[idStr]) {
          next[idStr] = "";
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, prod_id, prod2238FollowEnabled]);

  // --- LightLock Height Sync ---
  useEffect(() => {
    if (!open) return;

    const idsPresent = (rowsOps || [])
      .map((r) => Number(r.config_id))
      .filter((cid) => SPECIAL_HEIGHT_CONFIG_IDS.has(cid));
    if (idsPresent.length === 0) return;

    const desired = String(height_mm ?? "");
    setValuesOps((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const cid of idsPresent) {
        const key = String(cid);
        const cur = String(next[key] ?? "");
        if (cur !== desired) {
          next[key] = desired;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, height_mm, rowsOps]);

  // --- Pelmet Lock Sync ---
  const pelmetLockMap = useMemo(() => {
    if (Number(prod_id) !== 213) return {};
    const map = {};
    const idsPresent = new Set(
      (rowsOps || []).map((r) => Number(r.config_id))
    );
    for (const trig of PELMET_TRIGGERS) {
      if (!idsPresent.has(trig)) continue;
      const sel = String(valuesOps[String(trig)] ?? "");
      const target = PELMET_TRIGGER_TO_TARGET[trig];
      if (!target || !idsPresent.has(Number(target))) continue;
      map[String(target)] = {
        value: containsNeedPelmet(sel)
          ? String(width_mm ?? "")
          : "0",
      };
    }
    return map;
  }, [prod_id, rowsOps, valuesOps, width_mm]);

  useEffect(() => {
    if (!open) return;
    if (!pelmetLockMap || Object.keys(pelmetLockMap).length === 0)
      return;
    setValuesOps((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [idStr, cfg] of Object.entries(pelmetLockMap)) {
        const desired = String(cfg.value ?? "");
        if (next[idStr] !== desired) {
          next[idStr] = desired;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, pelmetLockMap]);

  // --- Unified 2-on-1 Panel Snapshot ---
  useEffect(() => {
    if (!open) return;
    if (!HEADRAIL_2ON1_REGISTRY[Number(prod_id)]) {
      setSplitPanelSizes(null);
      return;
    }
    setSplitPanelSizes(splitPanelRule.panels || null);
  }, [open, prod_id, splitPanelRule.panels]);

  // --- Unified 2-on-1 Lock Sync ---
  useEffect(() => {
    if (!open) return;
    const map = splitPanelRule.lockMap;
    if (!map || Object.keys(map).length === 0)
      return;
    setValuesOps((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [idStr, cfg] of Object.entries(map)) {
        const desired = String(cfg.value ?? "");
        if (next[idStr] !== desired) {
          next[idStr] = desired;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, splitPanelRule.lockMap]);

  // --- Prod 315 BO Fabric Lock ---
  useEffect(() => {
    if (!open) return;
    if (!isProd315BOFabric) return;
    if (!rowsOps || rowsOps.length === 0) return;

    setValuesOps((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const r of rowsOps) {
        const idNum = Number(r.config_id);
        if (!PROD_315_BO_DISABLED_OPS.has(idNum)) continue;
        const key = String(idNum);
        const def =
          r && r.def_value != null ? String(r.def_value) : "";
        if ((next[key] ?? "") !== def) {
          next[key] = def;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, isProd315BOFabric, rowsOps, setValuesOps]);

  // --- Prod 1945 Cable Guide Validation ---
  useEffect(() => {
    if (Number(prod_id) !== 1945) return;
    const h = Number(height_mm || 0);
    if (!Number.isFinite(h) || h <= 3500) return;
    const pick = (id) =>
      String(values?.[String(id)] || valuesOps?.[String(id)] || "").trim();
    const sideGuideOpt = [1708, 1712]
      .map((id) => pick(id))
      .find((v) => v && v.length > 0);
    const bottomBarOpt = [768, 1724, 1725, 1726, 1727, 3519]
      .map((id) => pick(id))
      .find((v) => v && v.length > 0);
    const msg =
      "Nhà sản xuất khuyến nghị chiều cao >3500mm cần có cáp dẫn bên (chọn giá đỡ lắp Sàn hoặc Tường) để đảm bảo hoạt động ổn định. Bạn cần sử dụng tùy chọn '18x35cm Hình chữ nhật (Rectangular)' trong cấu hình 'Loại thanh đáy (Bottom bar type)' kết hợp với tùy chọn ray dẫn cáp.";
    let invalid = false;
    // Rule 1: Height > 3500 + 'No need guide'
    if (sideGuideOpt && /không\s*cần\s*cáp/i.test(sideGuideOpt)) {
      invalid = true;
      window.alert(msg);
    }
    // Rule 2: Has cable guide but bottom bar not rectangular
    if (
      !invalid &&
      sideGuideOpt &&
      !/không\s*cần\s*cáp/i.test(sideGuideOpt) &&
      bottomBarOpt &&
      !/18x35\s*cm/i.test(bottomBarOpt) &&
      !/hình\s*chữ\s*nhật/i.test(bottomBarOpt)
    ) {
      invalid = true;
      window.alert(msg);
    }
    setAllowContinue((prev) => {
      if (prev === !invalid) return prev;
      return !invalid;
    });
  }, [prod_id, height_mm, values, valuesOps]);

// --- Prod 2238 Duolite Hint ---
  useEffect(() => {
    if (!open) return;
    if (hasShown2238DuoliteHint) return;
    if (Number(prod_id) !== 2238) return;

    const fabric = String(selectedFabricName || "").trim();
    if (!fabric) return;
    // ops of interest: 1814, 541, 543, 1820
    const duoliteOps = [1814, 541, 543, 1820];
    const hasTargetOp =
      Array.isArray(opIdsForQuery) &&
      opIdsForQuery.some((id) => duoliteOps.includes(Number(id)));
    if (!hasTargetOp) return;
    if (typeof window !== "undefined") {
      window.alert(
        'Vui lòng lưu ý chọn cùng một kích thước honeycomb (¾" ⅜" 1¼") cho panel Duolite ở bước sau (Sheer ¾", non-Sheer ¾" v.v.).'
      );
    }
    setHasShown2238DuoliteHint(true);
  }, [open, prod_id, selectedFabricName, opIdsForQuery, hasShown2238DuoliteHint]);

  // --- Coupled Warning Trigger (Once per session) ---
  useEffect(() => {
    if (!open) return;
    if (hasShownCoupledWarning) return;

    // Detect if ANY coupled logic is active (covers 305, 306, 2635, 493, 1945)
    // specialYesSelected covers 305, 306, 2635
    // exwTransform493 covers 493, 1945
    const isCoupledActive = specialYesSelected || !!exwTransform493;

    if (isCoupledActive) {
      setShowCoupledWarning(true);
      setHasShownCoupledWarning(true);
    }
  }, [open, specialYesSelected, exwTransform493, hasShownCoupledWarning]);

  // --- FST Payload Cleanup ---
  useEffect(() => {
    if (!specialYesSelected && fstPayload) {
      setFstPayload(null);
      setShowFstHeadless(false);
    }
  }, [specialYesSelected, fstPayload]);
  // ============================================================================
  // BUSINESS RULES SYNC EFFECTS - END
  // ============================================================================

  // ============================================================================
  // EVENT HANDLERS - START
  // ============================================================================
  /** field updaters */
const updateValue = (configId, newVal) => {
  const idNum = Number(configId);

    // prod 315: when Fabric Collection (2385) contains "BO",
  // keep these ops configs locked at their default value.
  if (
    Number(prod_id) === 315 &&
    PROD_315_BO_DISABLED_OPS.has(idNum) &&
    /\bbo\b/i.test(String(selectedFabricName || ""))
  ) {
    return;
  }
  
  let next = newVal;

  // Special validation for Side Alu CH (required/default config)
  if (SIDE_ALU_CONFIG_IDS.has(idNum)) {
    const text = String(newVal || "").toLowerCase();
    const h = Number(height_mm);

    if (
      text.includes(SIDE_ALU_CH_TOKEN) &&
      Number.isFinite(h) &&
      h > SIDE_ALU_CH_MAX_HEIGHT_MM
    ) {
      // Find the default value from rows (SQL #1 result)
      const row = (rows || []).find(
        (r) => Number(r.config_id) === idNum
      );
      const def =
        row && row.def_value != null
          ? String(row.def_value)
          : "";

      if (typeof window !== "undefined") {
        window.alert(
          'Tùy chọn "Kênh nhôm bên 22x55mm" không áp dụng cho chiều cao vượt quá 6500mm.'
        );
      }

      next = def; // revert to default (or empty if none)
    }
  }

  setValues((prev) => ({
    ...prev,
    [String(configId)]: next,
  }));
};

const updateValueOps = (configId, newVal) => {
  if (SPECIAL_HEIGHT_CONFIG_IDS.has(Number(configId))) return;
  if (PROD_210_CURVED_CONFIG_IDS.has(Number(configId))) return;
  if (pelmetLockMap[String(configId)]) return;
  if (splitPanelRule.lockMap[String(configId)]) return;

  const idNum = Number(configId);
  let next = newVal;

  // Special validation for Side Alu CH (ops config)
  if (SIDE_ALU_CONFIG_IDS.has(idNum)) {
    const text = String(newVal || "").toLowerCase();
    const h = Number(height_mm);

    if (
      text.includes(SIDE_ALU_CH_TOKEN) &&
      Number.isFinite(h) &&
      h > SIDE_ALU_CH_MAX_HEIGHT_MM
    ) {
      // Find the default value from operation-specific rows
      const row = (rowsOps || []).find(
        (r) => Number(r.config_id) === idNum
      );
      const def =
        row && row.def_value != null
          ? String(row.def_value)
          : "";

      if (typeof window !== "undefined") {
        window.alert(
          'Tùy chọn "Kênh nhôm bên 22x55mm" không áp dụng cho chiều cao vượt quá 2000mm.'
        );
      }

      next = def; // revert to default (or empty)
    }
  }

  setValuesOps((prev) => ({
    ...prev,
    [String(configId)]: next,
  }));
};

    /** Download action */
  const handleDownload = () => {
    if (!allValid) return;

    downloadProductOrderConfig({
      prod_id,
      prod_label,
      operation_id,
      operation_label,
      width_mm,
      height_mm,
      selectedOpIds: opIdsForQuery,

      // pass the raw rows + values; DownloadText.js handles gating & specials
      reqRows: rows,
      reqValues: values,
      opsRows: rowsOps,
      opsValues: valuesOps,

      // SizeCheck flow: no SRP section here
      isPurchaser: false,
      srpCalc: null,
    });
  };

// Retail flow orchestrator
  const startRetailFlow = () => {
    // --- Extra validation: prod 2237 + op 675 → must be OB style ---
    if (Number(prod_id) === 2237 && Array.isArray(opIdsForQuery) && opIdsForQuery.includes(675)) {
      const v2196 = String(valuesOps["2196"] ?? "").trim();
      if (v2196 !== "Ngoài Khung cửa sổ (OB)") {
        window.alert("Vận hành này (Traveling Wand) chỉ áp dụng cho kiểu lắp đặt 'Ngoài Khung cửa sổ (OB)'.");
        return; // stop here
      }
    }

    // [MODIFIED] Added 2181, 2845 to Headless Flow triggers (EXW Surcharge Route)
    const headlessTriggers = [305, 306, 2635, 2181, 2845];
    
    if (headlessTriggers.includes(Number(prod_id)) && specialYesSelected) {
      setShowFstHeadless(true);
    } else {
      setShowRetailPrice(true);
    }
  };

  // FST callback
  const handleFstContinue = ({
    exwTransform,
    selectedOpIdsAdjusted,
  }) => {
    setFstPayload({
      exwTransform: exwTransform || null,
      selectedOpIdsAdjusted:
        selectedOpIdsAdjusted || opIdsForQuery,
    });
    setShowFstHeadless(false);
    setShowRetailPrice(true);
  };

  // ============================================================================
  // EVENT HANDLERS - END
  // ============================================================================

// --- Show Cart Handler ---
  const handleShowCart = () => {
    setShowCartModal(true);
  };

// --- Add to Cart Handler ---
// --- NEW: Add to Cart Handler (Starts Headless Calc) ---
  const handleAddToCart = async () => {
    if (!allValid) return;
    // Trigger Headless Calculation via RetailModal
    setCalculatingCart(true);
  };

  // --- NEW: Callback when RetailModal finishes calculating ---
  const onCartCalculationComplete = async (costData) => {
    // costData = { exwork, ops_sc, ship_sc }
    setCalculatingCart(false); // Stop rendering the headless modal
    await saveToCartWithCosts(costData);
  };

// --- MOVED: The original saving logic, now accepting costData ---
  const saveToCartWithCosts = async (costData) => {
    const { exwork, ops_sc, ship_sc, audit = {}, srp_usd = 0 } = costData;

    // 1. Calculate Packing Dimensions
    const wMeters = (Number(width_mm) || 0) / 1000;
    const hMeters = (Number(height_mm) || 0) / 1000;
    
    // We use the first selected Operation ID for the hardware logic
    const calcOpId = (opIdsForQuery && opIdsForQuery.length > 0) ? Number(opIdsForQuery[0]) : 0;

    // Run the calculation (Returns an Array of packages)
    const packingResults = PackingCalculator.calculate(prod_id, calcOpId, wMeters, hMeters);

    // [NEW] Generate the beautifully formatted text snapshot for Bulk Cargo Quotes
    let snapshotLines = [];
    if (rows && rows.length > 0) {
      snapshotLines.push("    > Cấu hình mặc định (Default Configuration):");
      rows.forEach(r => {
        // Skip Collection (2385) and Color (2389) as they have dedicated columns
        if (Number(r.config_id) === 2385 || Number(r.config_id) === 2389) return;
        const v = String(values[String(r.config_id)] || "").trim();
        if (v) snapshotLines.push(`      - ${r.config}: ${v}`);
      });
    }
    if (rowsOps && rowsOps.length > 0) {
      // Add an explicit empty line separator if the default section already added items
      if (snapshotLines.length > 0) {
        snapshotLines.push("");
      }
      snapshotLines.push("    > Cấu hình vận hành (Operation Configuration):");
      rowsOps.forEach(r => {
        const v = String(valuesOps[String(r.config_id)] || "").trim();
        if (v) snapshotLines.push(`      - ${r.config}: ${v}`);
      });
    }
    const configSnapshotStr = snapshotLines.length > 0 ? ("\n" + snapshotLines.join("\n")) : "";

    setLoading(true);

    // [MODIFIED] Use Sequential Saving instead of Promise.all to prevent race conditions
    const savedIds = [];
    let errorOccurred = false;

    try {
      for (const [index, pack] of packingResults.entries()) {
        
        // Construct a label suffix to distinguish boxes
        let labelSuffix = "";
        if (packingResults.length > 1) {
             labelSuffix = ` [Box ${index + 1}: ${pack.description}]`;
        }

        const payload = {
          scope: "order_log",
          action: "save",

// Core Info
          prod_id: Number(prod_id),
          // [MOD] Trim label: Take text before "—" to shorten it
          prod_label: String(prod_label || "").split("—")[0].trim() + labelSuffix, 
          width_mm: Number(width_mm),
          height_mm: Number(height_mm),
          qty: qty, 
          suggested_price: Number(srp_usd),
          // Use the state 'values' which holds the selections for config 2385 and 2389
          fab_collect: String(values["2385"] || ""),
          fab_color: String(values["2389"] || ""), 
          prod_ops: String(operation_label || "").split("(")[0].trim(), // [NEW] Capture name before the ID code

          // Packing Info (Specific to THIS box)
          dim_l: pack.length,
          dim_w: pack.width,
          dim_h: pack.height,
          weight: pack.weight,

          // Master Carton Component Info
          carton_dim_l_mm: values["carton_dim_l_mm"] || 0,
          carton_dim_w_mm: values["carton_dim_w_mm"] || 0,
          carton_dim_h_mm: values["carton_dim_h_mm"] || 0,
          carton_gross_weight_kg: values["carton_gross_weight_kg"] || 0,
          pcs_per_carton: values["pcs_per_carton"] || 1,

          // Costs
          exwork: exwork,
          ops_sc: ops_sc,
          ship_sc: ship_sc,

          // [NEW] Audit Fields
          calc_val_a: audit.val_a || 0,
          calc_val_c: audit.val_c || 0,
          calc_val_d: audit.val_d || 0,
          calc_tax_amt: audit.val_tax_amt || 0,
          calc_proc_amt: audit.val_proc_amt || 0,

          // Config Snapshot
          reqValues: values,       
          opsValues: valuesOps,    
          selectedOpIds: opIdsForQuery, 
          limit_by_code: limit_by_code || "HW",
          config_snapshot: configSnapshotStr, // [NEW] Pass the generated string to DB
          
          panelSizes: splitPanelSizes || null
        };

        // Await each request individually
        const res = await fetch("/api/data", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok || !data.ok) {
          throw new Error(data.error || `Failed to save Box ${index + 1}`);
        }

        savedIds.push(data.order_id);
      }

      // Success
      alert(`✅ Đã lưu ${savedIds.length} kiện hàng thành công!\nMã đơn (Order IDs): ${savedIds.join(", ")}`);

    } catch (e) {
      console.error(e);
      // If we saved some but not all, let the user know
      if (savedIds.length > 0) {
        alert(`⚠️ Lưu thất bại một phần!\nĐã lưu được: ${savedIds.join(", ")}\nLỗi ở kiện tiếp theo: ${e.message}`);
      } else {
        alert("❌ Lỗi kết nối server (Network Error)");
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // RENDER FUNCTIONS - START
  // ============================================================================
  const renderField = (r) => {
    const idStr = String(r.config_id);
    const label = r.config || `Config ${idStr}`;
    const opts = String(r.options || "");

  // prod 2238 + op 1823: hide 2385 / 2389 after Duolite is selected on 2807
  const idNum = Number(r.config_id);
  if (
    is2238WithOp1823 &&
    CONFIG_2238_DUOLITE_GATED_DEFAULT_IDS.has(idNum) &&
    duoliteSelectedFor2807
  ) {
    // Once Duolite is chosen, hide these required configs
    return null;
  }    

    if (isFormToken(opts)) {
      const v = valueFromFormToken(opts, formCtx);
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            value={v}
            readOnly
            style={{ width: "100%", opacity: 0.8 }}
          />
        </div>
      );
    }

    // Prod 157 Wood / Faux color dropdown for config 2389
    if (Number(prod_id) === 157 && idNum === 2389) {
      const v = values[idStr] ?? "";
      const choices = prod157Colors.length > 0 ? ["", ...prod157Colors] : [""];
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <SelectInput
            value={v}
            onChange={(e) => updateValue(idStr, e.target.value)}
            choices={choices}
            style={{ width: "100%" }}
          />
        </div>
      );
    }

    // Prod 209 color dropdown for config 2389 (with 2385 mirroring)
    if (Number(prod_id) === 209 && idNum === 2389) {
      const v = values[idStr] ?? "";
      const choices = prod209Colors.length > 0 ? ["", ...prod209Colors] : [""];
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <SelectInput
            value={v}
            onChange={(e) => {
              const val = e.target.value;
              setValues((prev) => ({
                ...prev,
                [idStr]: val,
                "2385": val, // Mirror to 2385 for order logs & text downloads
              }));
            }}
            choices={choices}
            style={{ width: "100%" }}
          />
        </div>
      );
    }

    if (isTextInput(opts)) {
      const v = values[idStr] ?? "";
      return (
        <div key={idStr}>
          <TextInput
            label={label}
            value={v}
            onChange={(e) =>
              updateValue(idStr, e.target.value)
            }
            type="text"
          />
        </div>
      );
    }

    if (isDigitInput(opts)) {
      const v = values[idStr] ?? "";
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={v}
            onChange={(e) => {
              let val = e.target.value;
              if (/^\d*(?:\.\d{0,2})?$/.test(val)) {
                updateValue(idStr, val);
              }
            }}
            onBlur={(e) => {
              const trimmed = String(
                e.target.value || ""
              ).trim();
              if (!trimmed) return;
              let normalized = trimmed.replace(/\.$/, "");
              if (isValid2dpNumber(normalized)) {
                updateValue(idStr, normalized);
              } else {
                updateValue(idStr, "");
              }
            }}
            style={{ width: "100%" }}
          />
          <div
            style={{
              fontSize: 12,
              opacity: 0.7,
              marginTop: 4,
            }}
          >
            Định dạng: số dương, tối đa 2 chữ số thập phân
            (vd: 123, 123.45)
          </div>
        </div>
      );
    }

    // Fabric collection 2385
    if (Number(r.config_id) === 2385) {
      const v = values[idStr] ?? "";
      const choices =
        fabricChoices.length > 0
          ? ["", ...fabricChoices]
          : [""];
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <SelectInput
            value={v}
            onChange={(e) =>
              updateValue(idStr, e.target.value)
            }
            choices={choices}
            style={{ width: "100%" }}
          />
        </div>
      );
    }
    
    // Default dropdown (single select)
    const v = values[idStr] ?? "";
    const choices = splitChoices(opts);
    return (
      <div key={idStr}>
        <label
          style={{
            display: "block",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {label}
        </label>
        <SelectInput
          value={v}
          onChange={(e) =>
            updateValue(idStr, e.target.value)
          }
          choices={choices}
          style={{ width: "100%" }}
        />
      </div>
    );
  };

  const renderFieldOps = (r) => {
    const idStr = String(r.config_id);
    const idNum = Number(r.config_id);
    const label = r.config || `Config ${idStr}`;
    const opts = String(r.options || "");

    // prod 2238 + op 1823: hide gated configs unless Duolite option is chosen on 2807
    if (
      is2238WithOp1823 &&
      CONFIG_2238_DUOLITE_GATED_OPS_IDS.has(idNum) &&
      !duoliteSelectedFor2807
    ) {
      // Do not render these configs until user picks a Duolite option on 2807
      return null;
    }

    // --- prod 1323 Décor follow-up control ---
    const isDecorFollowUp = DECOR_FOLLOWUPS.has(idNum);
    const decorFollowUpEnabled = isDecorFollowUp ? !!decorEnabled[idNum] : false;

    // prod 2238 '>2743 mm' follow-up control
    const isProd2238FollowUp = PROD_2238_FOLLOWUPS.has(idNum);
    const prod2238FollowUpEnabled = isProd2238FollowUp
      ? !!prod2238FollowEnabled[idNum]
      : false;

  // LightLock special: show height_mm in a read-only text input (config_id 2426)
  if (SPECIAL_HEIGHT_CONFIG_IDS.has(idNum)) {
    const v = valuesOps[idStr] ?? String(height_mm ?? "");
    return (
      <div key={idStr}>
        <label
          style={{
            display: "block",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {label}
        </label>
        <input
          className="text-input"
          type="text"
          value={v}
          readOnly
          style={{ width: "100%", opacity: 0.8 }}
        />
        <div
          style={{
            fontSize: 12,
            opacity: 0.7,
            marginTop: 4,
          }}
        >
          Tự động lấy theo Chiều cao (mm). Chỉ dùng để tính LightLock, không cần chỉnh.
        </div>
      </div>
    );
  }

  // Prod 210 Curved Track Workmanship locked surcharge display (3673, 3674)
  if (Number(prod_id) === 210 && PROD_210_CURVED_CONFIG_IDS.has(idNum)) {
    const v = valuesOps[idStr] ?? "0";
    return (
      <div key={idStr}>
        <label
          style={{
            display: "block",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {label}
        </label>
        <input
          className="text-input"
          type="text"
          value={v}
          readOnly
          style={{ width: "100%", opacity: 0.8 }}
        />
        <div
          style={{
            fontSize: 12,
            opacity: 0.7,
            marginTop: 4,
          }}
        >
          Tự động tính phụ phí uốn cong ray (Curved track surcharge) theo công thức và chiều dài (mm).
        </div>
      </div>
    );
  }

    // pelmet locked targets
    if (pelmetLockMap[idStr]) {
      const lockedVal = pelmetLockMap[idStr].value;
      const isWidth = lockedVal !== "0";
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            value={lockedVal}
            readOnly
            style={{
              width: "100%",
              opacity: 0.8,
            }}
          />
          <div
            style={{
              fontSize: 12,
              opacity: 0.7,
              marginTop: 4,
            }}
          >
            {isWidth
              ? "Tự động theo Chiều rộng (mm) vì lựa chọn có 'Need Pelmet'. Không thể chỉnh sửa."
              : "Tự động đặt về 0 vì lựa chọn không có 'Need Pelmet'. Không thể chỉnh sửa."}
          </div>
        </div>
      );
    }

    // Unified 2-on-1 Headrail locked halves
    if (splitPanelRule.lockMap[idStr]) {
      const cfg = splitPanelRule.lockMap[idStr];
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            value={cfg.value}
            readOnly
            style={{
              width: "100%",
              opacity: 0.8,
            }}
          />
          <div
            style={{
              fontSize: 12,
              opacity: 0.7,
              marginTop: 4,
            }}
          >
            {cfg.reason ||
              "Tự động theo chiều rộng (mm). Không thể chỉnh sửa."}
          </div>
        </div>
      );
    }

    if (isFormToken(opts)) {
      const v = valueFromFormToken(opts, formCtx);
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            value={v}
            readOnly
            style={{ width: "100%", opacity: 0.8 }}
          />
        </div>
      );
    }

// --- Prod 157 2-on-1 Panel Width Inputs ---
    if (Number(prod_id) === 157 && PROD_157_2ON1_CONFIG_IDS.has(idNum)) {
      const W = toIntMm(width_mm);
      const netWidth = W != null ? W - PROD_157_GAP_MM : 0;
      const defaultLeft = netWidth > 0 ? Math.floor(netWidth / 2) : 0;
      const defaultRight = netWidth > 0 ? netWidth - defaultLeft : 0;

      let pairDef = 0;
      for (const pair of PROD_157_2ON1_PAIRS) {
        if (idNum === pair.left) { pairDef = defaultLeft; break; }
        if (idNum === pair.right) { pairDef = defaultRight; break; }
      }

      // Automatically display balanced value if state is uninitialized, empty, or "0"
      const rawVal = valuesOps[idStr];
      const v = (rawVal != null && rawVal !== "" && rawVal !== "0")
        ? rawVal
        : (pairDef > 0 ? String(pairDef) : (rawVal ?? ""));

      const err = rule157_2on1.errors[idStr] || null;

      // Locate pair error if this is the left panel
      let pairErr = null;
      for (const pair of PROD_157_2ON1_PAIRS) {
        if (idNum === pair.left) {
          pairErr = rule157_2on1.pairErrors[`${pair.left}-${pair.right}`] || null;
          break;
        }
      }

      return (
        <div key={idStr}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            inputMode="numeric"
            placeholder="0"
            value={v}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                const num = toIntMm(val);
                const netW = W != null ? W - PROD_157_GAP_MM : null;

                // Locate twin panel config ID
                let twinId = null;
                for (const pair of PROD_157_2ON1_PAIRS) {
                  if (idNum === pair.left) { twinId = pair.right; break; }
                  if (idNum === pair.right) { twinId = pair.left; break; }
                }

                // Dynamically adjust twin panel when a positive number is typed
                if (num != null && netW != null && twinId != null && num > 0 && num < netW) {
                  const twinVal = netW - num;
                  setValuesOps((prev) => ({
                    ...prev,
                    [idStr]: String(num),
                    [String(twinId)]: String(twinVal),
                  }));
                } else {
                  setValuesOps((prev) => ({
                    ...prev,
                    [idStr]: val,
                  }));
                }
              }
            }}
            style={{ width: "100%" }}
          />
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            Khoảng hở giữa 2 cánh: {PROD_157_GAP_MM} mm. Giới hạn diện tích cánh: tối đa {PROD_157_MAX_PANEL_M2} m².
          </div>
          {err && (
            <div style={{ color: "crimson", fontSize: 12, marginTop: 4 }}>
              {err}
            </div>
          )}
          {pairErr && (
            <div style={{ color: "crimson", fontSize: 12, marginTop: 4 }}>
              {pairErr}
            </div>
          )}
        </div>
      );
    }

if (isTextInput(opts)) {
  const v = valuesOps[idStr] ?? "";
  // Décor follow-ups: enable/disable + required note
  if (isDecorFollowUp && Number(prod_id) === 1323) {
    return (
      <div key={idStr} style={{ opacity: decorFollowUpEnabled ? 1 : 0.6 }}>
        <TextInput
          label={
            decorFollowUpEnabled
              ? `${label} (bắt buộc khi chọn Décor tape)`
              : `${label} (tự động tắt nếu không chọn Décor tape)`
          }
          value={v}
          onChange={(e) => {
            if (!decorFollowUpEnabled) return;
            setValuesOps((prev) => ({ ...prev, [idStr]: e.target.value }));
          }}
          type="text"
          disabled={!decorFollowUpEnabled}
        />
      </div>
    );
  }

  // Normal ops text input (unchanged)
  return (
    <div key={idStr}>
      <TextInput
        label={label}
        value={v}
        onChange={(e) => setValuesOps((prev) => ({ ...prev, [idStr]: e.target.value }))}
        type="text"
      />
    </div>
  );
}

    if (isDigitInput(opts)) {
      const v = valuesOps[idStr] ?? "";
      const isSplitProduct = !!HEADRAIL_2ON1_REGISTRY[Number(prod_id)];
      const err = isSplitProduct ? (splitPanelRule.errors[idStr] || null) : null;

      let pairErr = null;
      if (isSplitProduct && splitPanelRule.activeTargets.has(idNum)) {
        const activeSets = HEADRAIL_2ON1_REGISTRY[Number(prod_id)] || [];
        for (const { targets } of activeSets) {
          const [L, R] = targets;
          const key = `${L}-${R}`;
          if (
            idNum === L &&
            splitPanelRule.pairErrors[key]
          ) {
            pairErr = splitPanelRule.pairErrors[key];
            break;
          }
        }
      }

      // --- prod 2238 '>2743 mm' follow-ups: enable/disable + required note (digit inputs) ---
      if (isProd2238FollowUp && Number(prod_id) === 2238) {
        const disabled = !prod2238FollowUpEnabled;

        return (
          <div key={idStr} style={{ opacity: disabled ? 0.6 : 1 }}>
            <label
              style={{
                display: "block",
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              {disabled
                ? `${label} (tự động tắt trừ khi chọn >2743 mm)`
                : `${label} (bắt buộc khi chọn >2743 mm)`}
            </label>
            <input
              className="text-input"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={v}
              disabled={disabled}
              onChange={(e) => {
                if (disabled) return;
                let val = e.target.value;
                if (/^\d*(?:\.\d{0,2})?$/.test(val)) {
                  updateValueOps(idStr, val);
                }
              }}
              onBlur={(e) => {
                if (disabled) return;
                const trimmed = String(e.target.value || "").trim();
                if (!trimmed) return;
                let normalized = trimmed.replace(/\.$/, "");
                if (isValid2dpNumber(normalized)) {
                  const active2235 =
                    Number(prod_id) === 2235 &&
                    rule2235Computed.activeTargets.has(idNum);
                  const active2236 =
                    Number(prod_id) === 2236 &&
                    rule2236.activeTargets.has(idNum);

                  if (active2235 || active2236) {
                    const mm = toIntMm(normalized);
                    updateValueOps(idStr, mm != null ? String(mm) : "");
                  } else {
                    updateValueOps(idStr, normalized);
                  }
                } else {
                  updateValueOps(idStr, "");
                }
              }}
              style={{ width: "100%" }}
            />
            <div
              style={{
                fontSize: 12,
                opacity: 0.7,
                marginTop: 4,
              }}
            >
              Định dạng: số dương, tối đa 2 chữ số thập phân
              (vd: 123, 123.45)
            </div>
            {err && (
              <div
                style={{
                  color: "crimson",
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {err}
              </div>
            )}
            {pairErr && (
              <div
                style={{
                  color: "crimson",
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {pairErr}
              </div>
            )}
          </div>
        );
      }

      // --- normal numeric fallback for all other cases ---
      return (
        <div key={idStr}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            {label}
          </label>
          <input
            className="text-input"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={v}
            onChange={(e) => {
              let val = e.target.value;
              if (/^\d*(?:\.\d{0,2})?$/.test(val)) {
                updateValueOps(idStr, val);
              }
            }}
            onBlur={(e) => {
              const trimmed = String(e.target.value || "").trim();
              if (!trimmed) return;
              let normalized = trimmed.replace(/\.$/, "");
              if (isValid2dpNumber(normalized)) {
                const isSplitProduct = !!HEADRAIL_2ON1_REGISTRY[Number(prod_id)];
                const isSplitTarget = isSplitProduct && splitPanelRule.activeTargets.has(idNum);

                if (isSplitTarget) {
                  const mm = toIntMm(normalized);
                  const totalW = toIntMm(width_mm);
                  const activeSets = HEADRAIL_2ON1_REGISTRY[Number(prod_id)] || [];
                  
                  // Locate twin panel target config ID
                  let twinId = null;
                  for (const set of activeSets) {
                    const [L, R] = set.targets.map(Number);
                    if (idNum === L) { twinId = R; break; }
                    if (idNum === R) { twinId = L; break; }
                  }

                  // Auto-populate complementary remainder if input is valid and under total width
                  if (mm != null && totalW != null && twinId != null && mm > 0 && mm < totalW) {
                    const twinVal = totalW - mm;
                    setValuesOps((prev) => ({
                      ...prev,
                      [idStr]: String(mm),
                      [String(twinId)]: String(twinVal),
                    }));
                  } else {
                    updateValueOps(idStr, mm != null ? String(mm) : "");
                  }
                } else {
                  updateValueOps(idStr, normalized);
                }
              } else {
                updateValueOps(idStr, "");
              }
            }}
            style={{ width: "100%" }}
          />
          <div
            style={{
              fontSize: 12,
              opacity: 0.7,
              marginTop: 4,
            }}
          >
            Định dạng: số dương, tối đa 2 chữ số thập phân
            (vd: 123, 123.45)
          </div>
          {err && (
            <div
              style={{
                color: "crimson",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {err}
            </div>
          )}
          {pairErr && (
            <div
              style={{
                color: "crimson",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {pairErr}
            </div>
          )}
        </div>
      );
    }

      // SHEER fabric dropdown for ops config 2813
  if (idNum === 2813) {
    const v = valuesOps[idStr] ?? "";
    const choices =
      sheerChoicesOps && sheerChoicesOps.length
        ? ["", ...sheerChoicesOps]
        : [""];

    return (
      <div key={idStr}>
        <label
          style={{
            display: "block",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {label}
        </label>
        <SelectInput
          value={v}
          onChange={(e) => updateValueOps(idStr, e.target.value)}
          choices={choices}
          style={{ width: "100%" }}
        />
      </div>
    );
  }

// Duolite dropdowns:
// - prod 533: configs 2513 & 2514
// - prod 2238: RD configs 1766, 1830, 2705, 2734
// - prod 1910: RD configs 3285, 2278, 3292, 2277, 2276, 2272
const isDuoliteDropdown =
  [2513, 2514].includes(idNum) ||
  (Number(prod_id) === 2238 && PROD_2238_RD_CONFIG_IDS.has(idNum)) ||
  (Number(prod_id) === 1910 && PROD_1910_RD_CONFIG_IDS.has(idNum));

if (isDuoliteDropdown) {
  const v = valuesOps[idStr] ?? "";
  const choices =
    duoliteChoices.length > 0
      ? ["", ...duoliteChoices]
      : [""];

  return (
    <div key={idStr}>
      <label
        style={{
          display: "block",
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <SelectInput
        value={v}
        onChange={(e) => updateValueOps(idStr, e.target.value)}
        choices={choices}
        style={{ width: "100%" }}
      />
    </div>
  );
}

// RD fabric dropdown for ops config 2815 (standalone)
if (idNum === 2815) {
  const v = valuesOps[idStr] ?? "";
  const choices =
    rdChoicesOps2815 && rdChoicesOps2815.length
      ? ["", ...rdChoicesOps2815]
      : [""];

  return (
    <div key={idStr}>
      <label
        style={{
          display: "block",
          fontWeight: 600,
          marginBottom: 4,
          }}
        >
          {label}
        </label>
        <SelectInput
          value={v}
          onChange={(e) => updateValueOps(idStr, e.target.value)}
          choices={choices}
          style={{ width: "100%" }}
        />
      </div>
    );
  }

// Default ops dropdown (single select)
const v = valuesOps[idStr] ?? "";
const choices = splitChoices(opts);

const disableForBOFabric315 =
  Number(prod_id) === 315 &&
  PROD_315_BO_DISABLED_OPS.has(idNum) &&
  /\bbo\b/i.test(String(selectedFabricName || ""));

return (
  <div key={idStr}>
    <label style={{ display: "block", marginBottom: 4 }}>
      {label}
    </label>
    <SelectInput
      value={v}
      onChange={(e) =>
        updateValueOps(idStr, e.target.value)
      }
      choices={choices}
      style={{ width: "100%" }}
      disabled={disableForBOFabric315}
    />
    {disableForBOFabric315 && (
      <div
        style={{
          fontSize: 12,
          marginTop: 4,
          opacity: 0.7,
        }}
      >
        Tùy chọn này được khóa khi vải BO được chọn.
      </div>
    )}
  </div>
);

  };

const footerView = (
    <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {/* Left Side: Show Cart */}
      <div>
        <Button
          onClick={handleShowCart}
          disabled={calculatingCart || loading} // Disable during processing
          style={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
        >
          Xem giỏ / Show Cart
        </Button>
      </div>

      {/* Right Side: Existing Actions */}
      <div style={{ display: "flex", gap: "8px" }}>
        <Button
          onClick={handleDownload}
          disabled={
            !allValid ||
            loading ||
            loadingOps ||
            calculatingCart || // Disable during processing
            !!error ||
            !!errorOps
          }
        >
          Tải về cấu hình / Download config
        </Button>

        <Button
          onClick={startRetailFlow}
          disabled={
            !allValid ||
            loading ||
            loadingOps ||
            calculatingCart || // Disable during processing
            !!error ||
            !!errorOps ||
            !allowContinue
          }
        >
          Tiếp tục / Continue
        </Button>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER FUNCTIONS - END
  // ============================================================================

  // ============================================================================
  // MAIN COMPONENT RENDER - START
  // ============================================================================
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cấu hình SP để đặt hàng"
      maxWidth={720}
      footer={footerView}
    >
      {/* body scrolls; footer is sticky */}
      <div style={{ display: "grid", gap: 10 }}>
        {/* Summary box */}
        <div
          className="box"
          style={{ margin: 0, position: "relative" }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Tóm tắt lựa chọn / Selection summary
          </div>

          {/* Guide button in corner */}
          <div
            style={{
              position: "absolute",
              right: 8,
              top: 4,
            }}
          >
            <Button onClick={() => setGuideOpen(true)}>HDSD</Button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              fontSize: 14,
            }}
          >
            <div>
              Chiều rộng / Width: <b>{width_mm}</b> mm
            </div>
            <div>
              Chiều cao / Height: <b>{height_mm}</b> mm
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              Dòng sản phẩm / Product line:{" "}
              <b title={`ID: ${prod_id || "-"}`}>
                {prod_label ||
                  `(ID: ${prod_id || "-"})`}
              </b>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              Vận hành / Operation:{" "}
              <b title={`ID: ${operation_id || "-"}`}>
                {operation_label ||
                  `(ID: ${operation_id || "-"})`}
              </b>
            </div>
          </div>
        </div>

        {/* Required Product Configuration */}
        <div className="box" style={{ margin: 0 }}>
          <div
            style={{
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Cấu hình bắt buộc / Required product
            configuration
          </div>
          {loading ? (
            <div
              style={{ fontSize: 13, opacity: 0.8 }}
            >
              Đang tải cấu hình…
            </div>
          ) : error ? (
            <div
              style={{
                color: "crimson",
                fontSize: 13,
              }}
            >
              Lỗi: {error}
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{ fontSize: 13, opacity: 0.8 }}
            >
              Không có dữ liệu cấu hình.
            </div>
          ) : (
            <div
              style={{ display: "grid", gap: 10 }}
            >
              {rows.map(renderField)}
            </div>
          )}
        </div>

        {/* Operation-specific configuration */}
        <div className="box" style={{ margin: 0 }}>
          <div
            style={{
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Cấu hình theo vận hành /
            Operation-specific configuration
          </div>
          {loadingOps ? (
            <div
              style={{ fontSize: 13, opacity: 0.8 }}
            >
              Đang tải cấu hình theo vận hành…
            </div>
          ) : errorOps ? (
            <div
              style={{
                color: "crimson",
                fontSize: 13,
              }}
            >
              Lỗi: {errorOps}
            </div>
          ) : rowsOps.length === 0 ? (
            <div
              style={{ fontSize: 13, opacity: 0.8 }}
            >
              Không có cấu hình theo vận hành / No
              operation-specific configuration.
            </div>
          ) : (
            <div
              style={{ display: "grid", gap: 10 }}
            >
              {rule157_2on1.totalError && (
                <div style={{
                  padding: "8px 12px",
                  backgroundColor: "#fee2e2",
                  color: "#b91c1c",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600
                }}>
                  ⚠️ {rule157_2on1.totalError}
                </div>
              )}
              {rowsOps.map(renderFieldOps)}
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.75,
                }}
              >
                • Tùy chọn / Optional. (Một số mục yêu
                cầu lựa chọn cho Duolite)
              </div>
            </div>
          )}
        </div>

{/* Add-to-cart example */}
        {/* Changed: Keep button visible but disabled during loading/calculating */}
        {allValid &&
          !error &&
          !errorOps && (
            <Button
              onClick={handleAddToCart}
              disabled={loading || loadingOps || calculatingCart}
              style={
                (loading || loadingOps || calculatingCart)
                  ? { opacity: 0.7, cursor: "wait" }
                  : {}
              }
            >
              {loading || calculatingCart
                ? "Đang xử lý / Processing..."
                : "Thêm vào giỏ / Add to cart"}
            </Button>
          )}
      </div>

{/* Headless FST runner */}
      <Suspense fallback={null}>
        {authChecked && (() => {
          // --- LOGIC: AUTO-INJECT CONFIGS FOR PROD 2845 (HEADLESS) ---
          let effectiveOpsRows = [...rowsOps];
          let effectiveOpsValues = { ...valuesOps };

          if (Number(prod_id) === 2845) {
            const w = Number(width_mm);
            const h = Number(height_mm);
            const TRIGGER_IDS = [2955, 2956, 2965, 2980, 3068];
            
            let selectedOpt = "";
            for (const id of TRIGGER_IDS) {
              const val = String(valuesOps[String(id)] || "").trim();
              if (val) { selectedOpt = val; break; }
            }

            let targetId = null;
            let targetLabel = "Auto Upgrade";

            if (selectedOpt === "Dooya Sensitivo (có remote)") {
               if (w >= 4000 && h < 4000)  { targetId = 3128; targetLabel = "Sensitivo Upgrade 1"; }
               else if (w >= 4000 && h >= 4000) { targetId = 3127; targetLabel = "Sensitivo Upgrade 2"; }
            }
            else if (selectedOpt === "Dooya Pro Sensitivo (có remote)") {
               if (w >= 4000 && h < 4000)  { targetId = 3126; targetLabel = "Pro Sensitivo Upgrade 1"; }
               else if (w >= 4000 && h >= 4000) { targetId = 3129; targetLabel = "Pro Sensitivo Upgrade 2"; }
            }
            else if (selectedOpt === "Somfy Maestria (có remote)") {
               if (w >= 4000 && h < 4000)  { targetId = 3136; targetLabel = "Maestria Upgrade 1"; }
               else if (w >= 4000 && h >= 4000) { targetId = 3137; targetLabel = "Maestria Upgrade 2"; }
            }

            if (targetId && !rowsOps.some(r => Number(r.config_id) === targetId)) {
               effectiveOpsRows.push({ config_id: targetId, config: targetLabel, options: "Auto", def_value: "Auto" });
               effectiveOpsValues[String(targetId)] = targetLabel; 
            }
          }
          // -------------------------------------------------------------

          return (
            <HeadlessFstModal
              open={showFstHeadless}
              onCancel={() => setShowFstHeadless(false)}
              onContinue={handleFstContinue}
              prodId={Number(prod_id)}
              width={Number(width_mm)}
              height={Number(height_mm)}
              prodLabel={String(prod_label || "")}
              operationId={Number(operation_id)}
              operationLabel={String(operation_label || "")}
              selectedOpIds={opIdsForQuery}
              valuesOps={effectiveOpsValues}
            />
          );
        })()}
      </Suspense>

      <Suspense fallback={null}>
        {authChecked && (() => {
          // --- LOGIC: AUTO-INJECT CONFIGS FOR PROD 2845 (RETAIL UI) ---
          // (Must repeat logic to ensure UI receives the same data as Headless)
          let effectiveOpsRows = [...rowsOps];
          let effectiveOpsValues = { ...valuesOps };

          if (Number(prod_id) === 2845) {
            const w = Number(width_mm);
            const h = Number(height_mm);
            const TRIGGER_IDS = [2955, 2956, 2965, 2980, 3068];
            
            let selectedOpt = "";
            for (const id of TRIGGER_IDS) {
              const val = String(valuesOps[String(id)] || "").trim();
              if (val) { selectedOpt = val; break; }
            }

            let targetId = null;
            let targetLabel = "Auto Upgrade";

            if (selectedOpt === "Dooya Sensitivo (có remote)") {
               if (w >= 4000 && h < 4000)  { targetId = 3128; targetLabel = "Sensitivo Upgrade 1"; }
               else if (w >= 4000 && h >= 4000) { targetId = 3127; targetLabel = "Sensitivo Upgrade 2"; }
            }
            else if (selectedOpt === "Dooya Pro Sensitivo (có remote)") {
               if (w >= 4000 && h < 4000)  { targetId = 3126; targetLabel = "Pro Sensitivo Upgrade 1"; }
               else if (w >= 4000 && h >= 4000) { targetId = 3129; targetLabel = "Pro Sensitivo Upgrade 2"; }
            }
            else if (selectedOpt === "Somfy Maestria (có remote)") {
               if (w >= 4000 && h < 4000)  { targetId = 3136; targetLabel = "Maestria Upgrade 1"; }
               else if (w >= 4000 && h >= 4000) { targetId = 3137; targetLabel = "Maestria Upgrade 2"; }
            }

            if (targetId && !rowsOps.some(r => Number(r.config_id) === targetId)) {
               effectiveOpsRows.push({ config_id: targetId, config: targetLabel, options: "Auto", def_value: "Auto" });
               effectiveOpsValues[String(targetId)] = targetLabel; 
            }
          }
          // -------------------------------------------------------------

          return (
            <PriceModal
              open={showRetailPrice}
              onClose={() => setShowRetailPrice(false)}
              summary={{
                mark_fabric_exw: needsFabricExwSpecial,
                width_mm,
                height_mm,
                prod_id,
                prod_label,
                operation_id,
                operation_label,
                selectedOpIds: (() => {
                  let base = fstPayload?.selectedOpIdsAdjusted &&
                    Array.isArray(fstPayload.selectedOpIdsAdjusted) &&
                    fstPayload.selectedOpIdsAdjusted.length > 0
                      ? fstPayload.selectedOpIdsAdjusted
                      : opIdsForQuery;
                  // [TUNNEL] Append Color ID for Prod 199 & 209 so RetailModal sends it to the server
                  return ((Number(prod_id) === 199 || Number(prod_id) === 209) && fabricPriceGroupId) ? [...base, fabricPriceGroupId] : base;
                })(),
                fabric_collection: selectedFabricName || undefined,
                fabric_price_group_id: fabricPriceGroupId,
                qty,
                limit_by_code: String(limit_by_code || "").toUpperCase() || undefined,
                opsRows: effectiveOpsRows,     // <--- INJECTED
                opsValues: effectiveOpsValues, // <--- INJECTED
                reqRows: rows,
                reqValues: values,
                ...(summaryExwTransform
                  ? { exwTransform: summaryExwTransform }
                  : {}),
                ...(splitPanelSizes ? { panelSizes: splitPanelSizes } : {}),
              }}
            />
          );
        })()}
      </Suspense>

      {/* NEW: Cart Feature Placeholder Modal */}
<Suspense fallback={null}>
  <CartModal 
    open={showCartModal} 
    onClose={() => setShowCartModal(false)} 
  />
</Suspense>

{/* --- NEW: Headless PriceModal for Cart Calculation --- */}
      <Suspense fallback={null}>
        {calculatingCart && (
          <PriceModal
            open={true} // Must be open to run logic
            isHeadless={true} // Hides UI, auto-runs calc
            onHeadlessComplete={onCartCalculationComplete}
            summary={{
              mark_fabric_exw: needsFabricExwSpecial,
              width_mm,
              height_mm,
              prod_id,
              prod_label,
              operation_id,
              operation_label,
              selectedOpIds: (() => {
                let base = fstPayload?.selectedOpIdsAdjusted &&
                  Array.isArray(fstPayload.selectedOpIdsAdjusted) &&
                  fstPayload.selectedOpIdsAdjusted.length > 0
                    ? fstPayload.selectedOpIdsAdjusted
                    : opIdsForQuery;
                // [TUNNEL] Append Color ID for Prod 199 so RetailModal sends it to the server
                return (Number(prod_id) === 199 && fabricPriceGroupId) ? [...base, fabricPriceGroupId] : base;
              })(),
              fabric_collection: selectedFabricName || undefined,
              fabric_price_group_id: fabricPriceGroupId,
              qty,
              limit_by_code: String(limit_by_code || "").toUpperCase() || undefined,
              opsRows: rowsOps,
              opsValues: valuesOps,
              reqRows: rows,
              reqValues: values,
              ...(summaryExwTransform ? { exwTransform: summaryExwTransform } : {}),
              ...(splitPanelSizes ? { panelSizes: splitPanelSizes } : {}),
            }}
          />
        )}
      </Suspense>

      {/* --- GUIDE MODAL (Context-Aware) --- */}
      <Modal
        open={guideOpen}
        title={activeGuide.title}
        onClose={() => setGuideOpen(false)}
        maxWidth={600}
      >
         <div className="box" style={{ margin: 0, border: "none", padding: 0 }}>
            <div style={{ marginBottom: 16 }}>
              {activeGuide.intro}
            </div>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {activeGuide.steps.map((line, i) => (
                <li key={i} style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
                  {line}
                </li>
              ))}
            </ul>
         </div>
      </Modal>

{/* --- COUPLED WARNING MODAL --- */}
      <Modal
        open={showCoupledWarning}
        title="Lưu ý quan trọng: Giá hệ Coupled"
        onClose={() => setShowCoupledWarning(false)}
        maxWidth={500}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setShowCoupledWarning(false)}>
              Đã hiểu / Understood
            </Button>
          </div>
        }
      >
        <div className="box" style={{ margin: 0, border: "none", padding: 0 }}>
          <p style={{ marginBottom: 12 }}>
            Bạn đã chọn cấu hình <b>Coupled Motor</b> (nhiều bộ rèm dùng chung 1 mô-tơ).
          </p>
          <p style={{ marginBottom: 8 }}>Vui lòng lưu ý về cách tính giá:</p>
          <ul style={{ paddingLeft: 20, margin: 0, lineHeight: "1.5" }}>
            <li style={{ marginBottom: 6 }}>
              <b>Vải rèm:</b> Giá hiển thị đã bao gồm toàn bộ các bộ rèm trong hệ này (đã nhân hệ số x2 hoặc x3).
            </li>
            <li style={{ marginBottom: 6 }}>
              <b>Mô-tơ:</b> Giá chỉ bao gồm chính xác <b>1 Mô-tơ</b> cho cả hệ thống.
            </li>
            <li style={{ marginBottom: 6 }}>
              <b>Số lượng (Qty):</b> Vui lòng giữ <b>Số lượng = 1</b> để đặt hàng cho 1 hệ thống hoàn chỉnh.
            </li>
          </ul>
          <p style={{ marginTop: 12, fontSize: "0.9em", fontStyle: "italic", color: "#666" }}>
            * Lưu ý: Chỉ tăng số lượng nếu bạn muốn đặt nhiều hệ thống riêng biệt.
          </p>
        </div>
      </Modal>
    </Modal>
  );

  // ============================================================================
  // MAIN COMPONENT RENDER - END
  // ============================================================================
}
// ============================================================================
// MAIN COMPONENT DEFINITION - END
// ============================================================================